import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useHideChrome } from "@/hooks/useChrome";
import {
  Plus, Trash2, Edit2, Copy, ArrowLeft, Save, BarChart2,
  RefreshCw, CheckCircle, XCircle, FileText, Settings,
  Inbox, Eye, Download, Search, AlertTriangle, Layers,
  Shield, Zap, Palette, Globe, Lock, ChevronRight, Code,
  Star, MessageSquare, Mail,
  ClipboardList, HelpCircle, UserCheck, Briefcase,
  ShoppingCart, Check, ThumbsUp, ThumbsDown, Clock, UserPlus, Building2,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { COUNTRY_NAMES } from "@/constants/countries";
import countriesStatesData from "@/constants/countriesStates.json";

import {
  Form, FormField, FormSubmission, FormAnalytics,
  FieldType, FIELD_PALETTE, DEFAULT_FORM, makeFieldName, makeUid,
  LAYOUT_TYPES, MultiLangValue, FieldOptionML, mlGet, mlSet, mlWrap,
} from "@/components/form-builder/types";
import {
  DEFAULT_LANGUAGES, Language, StoreLanguageConfig, getDirection,
} from "@/types/multilang";
import {
  buildMultiLangLabel, buildMultiLangPlaceholder, buildMultiLangOption,
} from "@/components/form-builder/formFieldTranslations";

import { FieldCard, FieldSettingsPanel } from "@/components/form-builder/FieldCard";
import { LanguageSelector } from "@/components/LanguageSelector";
import {
  GeneralTab, NotificationsTab, SecurityTab,
  StyleTab, IntegrationsTab, GdprTab, EmbedTab,
} from "@/components/form-builder/FormSettingsTabs";
import { ApprovalTab } from "@/components/form-builder/FormSettingsTabs.ApprovalTab";
import { RegistrationTab } from "@/components/form-builder/FormSettingsTabs.RegistrationTab";
import { Button } from "@/components/ui/Button";
import { ResizablePanel } from "@/components/ui/ResizablePanel";
import { ShortcodeGuideButton } from "@/components/ui";

import {
  fetchForms, fetchForm, saveForm, deleteForm, duplicateForm,
  fetchSubmissions, updateSubmissionStatus, deleteSubmission, exportCsv,
  fetchAnalytics,
  approveSubmission, rejectSubmission,
} from "@/services/formBuilder.service";
import { getStoreLanguages, getStoreInfo } from "@/services/api";
import { useDragDrop } from "@/components/form-builder/hooks/useDragDrop";
import { useThreeColumnLayout } from "@/hooks/useThreeColumnLayout";

type View = "list" | "template-picker" | "wizard-name" | "builder" | "submissions" | "analytics" | "settings";
type WizardStep = "name" | "build" | "settings" | "done";
type SettingsNav = "general" | "style" | "security" | "notifications" | "embed" | "integrations" | "gdpr" | "approval" | "registration";
type RightPanel = "field" | "preview";

// ─────────────────────────────────────────────────────────────────────────────
// Field factory
// ─────────────────────────────────────────────────────────────────────────────

function makeField(type: FieldType, langs: string[] = ["en"]): FormField {
  const meta = FIELD_PALETTE.find(f => f.type === type)!;
  const _id = makeUid();
  const label = buildMultiLangLabel(meta.label, langs);
  const empty = Object.fromEntries(langs.map(l => [l, ""]));
  const base: FormField = {
    _id, step: 1, type,
    label,
    name: makeFieldName(meta.label, _id),
    required: false, sort_order: 0, width: "full",
    options: [], validation: {}, has_conditions: false, conditions: {}, settings: {},
    placeholder: { ...empty }, helpText: { ...empty }, defaultValue: "",
  };

  const opt = (en: string): FieldOptionML => ({ label: buildMultiLangOption(en, langs), selected: false });
  const ph = (en: string) => buildMultiLangPlaceholder(en, langs);
  const lbl = (en: string) => buildMultiLangLabel(en, langs);

  switch (type) {
    case "text": return { ...base, placeholder: ph("Type here...") };
    case "email": return { ...base, label: lbl("Email Address"), placeholder: ph("you@example.com") };
    case "phone": return { ...base, label: lbl("Phone Number"), placeholder: ph("+1 (555) 000-0000") };
    case "textarea": return { ...base, label: lbl("Message"), placeholder: ph("Type your message..."), settings: { rows: 4 } };
    case "select": return { ...base, label: lbl("Select Option"), options: [opt("Option 1"), opt("Option 2"), opt("Option 3")] };
    case "multiselect": return { ...base, label: lbl("Select Multiple"), options: [opt("Option 1"), opt("Option 2")] };
    case "radio": return { ...base, label: lbl("Choose One"), options: [opt("Option A"), opt("Option B"), opt("Option C")] };
    case "checkbox": return { ...base, label: lbl("Choose All"), options: [opt("Choice 1"), opt("Choice 2"), opt("Choice 3")] };
    case "rating": return { ...base, label: lbl("Rate Experience"), settings: { maxStars: 5 } };
    case "slider": return { ...base, label: lbl("Select Value"), settings: { min: 0, max: 100, step: 1, unit: "", showImages: false, sliderImages: [{ label: "Low", img: "", value: 0 }, { label: "Mid", img: "", value: 50 }, { label: "High", img: "", value: 100 }] } };
    case "file": return { ...base, label: lbl("Upload File"), settings: { multiple: false, maxFiles: 1, accept: "*", showProgress: false } };
    case "signature": return { ...base, label: lbl("Signature") };
    case "hidden": return { ...base, label: lbl("Hidden Field") };
    case "heading": return { ...base, label: lbl("Section Heading") };
    case "paragraph": return { ...base, label: lbl("Paragraph") };
    case "spacer": return { ...base, settings: { height: 24 } };
    case "html": return { ...base, settings: { html: "<p>HTML block</p>" } };
    case "terms_checkbox": return {
      ...base,
      label: lbl("Terms & Conditions"),
      required: true,
      settings: {
        termsText: "I agree to the <a href='#' target='_blank'>Terms and Conditions</a> and <a href='#' target='_blank'>Privacy Policy</a>",
        termsRequired: true,
        termsUrl: "",
      },
    };
    default: return base;
  }
}

// Shopify's GraphQL userErrors often trail off into a "Learn more at
// https://shopify.dev/..." documentation link — strip that so the tooltip
// shows only the actual problem, not a raw URL the merchant can't act on.
function cleanShopifyErrorMessage(msg?: string | null): string {
  if (!msg) return "";
  return msg
    .replace(/\s*Learn more[^.]*\.?/gi, "")
    .replace(/https?:\/\/\S+/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// Live Preview
// ─────────────────────────────────────────────────────────────────────────────

const LivePreview: React.FC<{ form: Form; lang?: string }> = ({ form, lang = "en" }) => {
  const [ratings, setRatings] = useState<Record<string, number>>({});
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectValues, setSelectValues] = useState<Record<string, string>>({});
  const [textValues, setTextValues] = useState<Record<string, string>>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const sigRef = useRef<HTMLCanvasElement>(null);

  // Options for a "select" field with settings.dependsOnCountry set are
  // computed live from the chosen country in the linked field.
  const stateOptionsFor = (countryFieldName: string): string[] => {
    const countryName = selectValues[countryFieldName];
    if (!countryName) return [];
    const entry = (countriesStatesData as { country: string; states: string[] }[])
      .find((c) => c.country === countryName);
    return entry?.states ?? [];
  };

  useEffect(() => {
    const c = sigRef.current;
    if (!c) return;
    c.width = c.offsetWidth; c.height = c.offsetHeight;
    let drawing = false, lx = 0, ly = 0;
    const ctx = c.getContext("2d")!;
    ctx.strokeStyle = "#1a1a1a"; ctx.lineWidth = 1.5; ctx.lineCap = "round";
    const gp = (e: MouseEvent) => { const r = c.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    const dn = (e: MouseEvent) => { drawing = true; ({ x: lx, y: ly } = gp(e)); };
    const mv = (e: MouseEvent) => { if (!drawing) return; const p = gp(e); ctx.beginPath(); ctx.moveTo(lx, ly); ctx.lineTo(p.x, p.y); ctx.stroke(); lx = p.x; ly = p.y; };
    const up = () => { drawing = false; };
    c.addEventListener("mousedown", dn); c.addEventListener("mousemove", mv);
    c.addEventListener("mouseup", up); c.addEventListener("mouseleave", up);
    return () => { c.removeEventListener("mousedown", dn); c.removeEventListener("mousemove", mv); c.removeEventListener("mouseup", up); c.removeEventListener("mouseleave", up); };
  }, [form.fields.length, currentStep]);

  const ic = "w-full px-3 py-2 text-xs border border-ink-200 rounded-lg bg-white font-sans focus:border-brand-500 outline-none";

  if (done) return <div className="text-center py-8"><div className="text-3xl mb-2">✅</div><p className="text-sm font-semibold text-ink-900 font-sans">Thank you!</p></div>;

  const fields = form.fields;
  let steps: FormField[][] = [[]];
  if (!form.is_multi_step) {
    steps = [fields];
  } else {
    for (const field of fields) {
      if (field.type === 'divider') {
        if (steps[steps.length - 1].length > 0) steps.push([]);
        (steps[steps.length - 1] as any).__meta = {
          label: field.settings?.sectionLabel || '',
          style: field.settings?.sectionStyle || 'card',
        };
        continue;
      }
      steps[steps.length - 1].push(field);
    }
    steps = steps.filter(s => s.length > 0);
    if (steps.length === 0 && fields.length > 0) steps = [fields]; // fallback
  }

  const activeStepFields = steps[currentStep] || [];

  const validateStep = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;
    for (const f of activeStepFields) {
      if (f.required) {
        const isTextLike = !LAYOUT_TYPES.includes(f.type) && !["select", "multiselect", "radio", "checkbox", "rating", "file", "signature", "hidden", "terms_checkbox"].includes(f.type);
        let val = "";
        if (isTextLike || f.type === "textarea") val = textValues[f.name];
        else if (["select", "multiselect"].includes(f.type)) val = selectValues[f.name];
        else if (f.type === "rating") val = ratings[f.name] ? "yes" : "";
        else if (f.type === "terms_checkbox") val = textValues[f.name]; // check if we set it
        // simplified required check
        if (!val || val.length === 0) {
          isValid = false;
          newErrors[f.name] = (f.validation as any)?.messages?.required?.[lang] || "This field is required";
        }
      }
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(s => s + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(s => Math.max(0, s - 1));
    setErrors({});
  };

  const submit = () => {
    if (validateStep()) {
      setBusy(true);
      setTimeout(() => { setBusy(false); setDone(true); setTimeout(() => setDone(false), 2000); }, 1000);
    }
  };

  type Section = { divider?: typeof fields[0]; sectionFields: typeof fields };
  const sections: Section[] = [];
  let current: Section = { sectionFields: [] };

  activeStepFields.forEach((field) => {
    if (field.type === 'divider') {
      sections.push(current);
      current = { divider: field, sectionFields: [] };
    } else {
      current.sectionFields.push(field);
    }
  });
  sections.push(current);

  const sectionStyle = (style?: string) => {
    switch (style) {
      case 'bordered': return 'border border-ink-200 rounded-lg p-3 mb-2 overflow-hidden relative isolate';
      case 'plain': return 'py-2 mb-2 relative isolate';
      default: return 'bg-white border border-ink-200 rounded-lg shadow-sm p-3 mb-2 overflow-hidden relative isolate';
    }
  };

  const stepMeta = (steps[currentStep] as any)?.__meta;
  const isMultiStepActive = form.is_multi_step && steps.length > 1;

  return (
    <div>
      {form.show_title !== false && form.name && <h3 className="text-sm font-bold text-ink-900 font-sora mb-2">{mlGet(form.name as any, lang)}</h3>}
      {form.show_description !== false && !!mlGet(form.description as any, lang) && (
        <p className="text-xs text-ink-500 font-sans mb-3">{mlGet(form.description as any, lang)}</p>
      )}

      {isMultiStepActive && (
        <div className="mb-6 mt-2 px-2">
          <div className="flex items-center justify-between relative">
            {/* Background line connecting all steps */}
            <div className="absolute left-0 right-0 top-1/2 h-[2px] bg-ink-100 -translate-y-1/2 z-0" />

            {steps.map((step, idx) => {
              const isCompleted = idx < currentStep;
              const isCurrent = idx === currentStep;

              return (
                <div key={idx} className="relative z-10 flex flex-col items-center flex-1 first:items-start last:items-end">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-colors ${isCurrent || isCompleted
                      ? 'bg-brand-500 text-white'
                      : 'bg-white border-2 border-ink-200 text-ink-400'
                      }`}
                    style={isCurrent || isCompleted ? { backgroundColor: form.custom_styles?.primaryColor || '#ed5a29' } : {}}
                  >
                    {idx + 1}
                  </div>
                  {/* Active/Completed connecting line originating from this step to the right */}
                  {idx < steps.length - 1 && (isCompleted || isCurrent) && (
                    <div
                      className="absolute top-1/2 h-[2px] -translate-y-1/2 z-0 transition-all duration-300"
                      style={{
                        left: idx === 0 ? '16px' : '50%',
                        right: idx === steps.length - 2 ? '16px' : '-50%',
                        backgroundColor: isCompleted ? (form.custom_styles?.primaryColor || '#ed5a29') : 'transparent',
                        width: isCompleted ? 'auto' : '0%'
                      }}
                    />
                  )}
                  {/* Label */}
                  {(step as any)?.__meta?.label && (
                    <span className="absolute top-10 text-[10px] font-medium text-ink-500 whitespace-nowrap">
                      {(step as any).__meta.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={isMultiStepActive ? sectionStyle(stepMeta?.style) : ''}>
        {sections.map((section, sIdx) => (
          <div key={sIdx}>
            {section.divider && (
              <>
                <hr className="border-ink-200 my-2 mb-3" />
                {section.divider.settings?.sectionLabel && (
                  <p className="text-xs font-semibold text-ink-900 font-sans mb-2">{section.divider.settings.sectionLabel}</p>
                )}
              </>
            )}
            {section.sectionFields.length > 0 && (
              <div className={section.divider ? sectionStyle(section.divider.settings?.sectionStyle as string) : ''}>
                {(() => {
                  const elements = [];
                  const fields = section.sectionFields;

                  const renderField = (f: any, key: string, wrapperClass: string = "mb-3") => {
                    if (LAYOUT_TYPES.includes(f.type)) return (
                      <div key={key} className={wrapperClass}>
                        {f.type === "heading" && <p className="text-sm font-semibold text-ink-900 mt-1 mb-1 font-sans">{mlGet(f.label, lang)}</p>}
                        {f.type === "paragraph" && <p className="text-xs text-ink-500 mb-2 font-sans">{mlGet(f.label, lang)}</p>}
                        {f.type === "spacer" && <div style={{ height: f.settings.height ?? 12 }} />}
                      </div>
                    );
                    const error = errors[f.name];
                    const eClass = error ? "border-red-500 bg-red-50/30" : "mb-2";
                    return (
                      <div key={key} className={wrapperClass}>
                        <label className="block text-[11px] font-semibold text-ink-500 mb-1 font-sans">{mlGet(f.label, lang)}{f.required && <span className="text-red-500 ml-0.5">*</span>}</label>
                        {f.type === "textarea" && <textarea className={`${ic} ${eClass}`} rows={f.settings.rows ?? 3} placeholder={mlGet(f.placeholder, lang)} value={textValues[f.name] ?? ""} onChange={e => setTextValues(v => ({ ...v, [f.name]: e.target.value }))} />}
                        {(f.type === "select" || f.type === "multiselect") && (f.settings?.dependsOnCountry ? (
                          <select className={`${ic} ${eClass}`} value={selectValues[f.name] ?? ""} onChange={(e) => setSelectValues((v) => ({ ...v, [f.name]: e.target.value }))}>
                            <option value="">{selectValues[f.settings.dependsOnCountry as string] ? "Select..." : "Select a country first"}</option>
                            {stateOptionsFor(f.settings.dependsOnCountry as string).map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <select className={`${ic} ${eClass}`} multiple={f.type === "multiselect"} value={f.type === "multiselect" ? undefined : (selectValues[f.name] ?? "")} onChange={(e) => setSelectValues((v) => ({ ...v, [f.name]: e.target.value }))}>
                            <option value="">{mlGet(f.placeholder, lang) || "Select..."}</option>
                            {f.options.map((o: any, i: number) => { const opt = o as FieldOptionML; const lbl = mlGet(opt.label ?? (o as any), lang); return <option key={i} value={lbl} defaultChecked={!!opt.selected}>{lbl}</option>; })}
                          </select>
                        ))}
                        {f.type === "radio" && <div className="flex flex-col gap-1 mb-2">{f.options.map((o: any, i: number) => { const opt = o as FieldOptionML; const lbl = mlGet(opt.label ?? (o as any), lang); return <label key={i} className="flex items-center gap-2 text-xs font-sans cursor-pointer"><input type="radio" name={"pv_" + f.name} defaultChecked={!!opt.selected} className="accent-[var(--brand-500)]" />{lbl}</label>; })}</div>}
                        {f.type === "checkbox" && <div className="flex flex-col gap-1 mb-2">{f.options.map((o: any, i: number) => { const opt = o as FieldOptionML; const lbl = mlGet(opt.label ?? (o as any), lang); return <label key={i} className="flex items-center gap-2 text-xs font-sans cursor-pointer"><input type="checkbox" defaultChecked={!!opt.selected} className="accent-[var(--brand-500)]" />{lbl}</label>; })}</div>}
                        {f.type === "rating" && <div className="flex gap-1 mb-2">{Array.from({ length: f.settings.maxStars ?? 5 }).map((_, si) => <button key={si} type="button" onClick={() => setRatings(r => ({ ...r, [f.name]: si + 1 }))} className={(ratings[f.name] ?? 0) > si ? "text-amber-400 text-xl" : "text-borderGreyColor text-xl"}>★</button>)}</div>}
                        {f.type === "slider" && <div className="mb-2"><input type="range" min={f.settings.min ?? 0} max={f.settings.max ?? 100} step={f.settings.step ?? 1} className="w-full" style={{ accentColor: "var(--brand-500)" }} /><div className="flex justify-between text-[10px] text-ink-500"><span>{f.settings.min ?? 0}{f.settings.unit}</span><span>{f.settings.max ?? 100}{f.settings.unit}</span></div></div>}
                        {f.type === "file" && <div className={`border-2 border-dashed ${error ? 'border-red-300' : 'border-ink-200'} rounded-lg p-3 text-center text-xs text-ink-500 mb-2 cursor-pointer`}>📎 Drop file</div>}
                        {f.type === "signature" && <div className={`border ${error ? 'border-red-300' : 'border-ink-200'} rounded-lg overflow-hidden mb-2`}><div className="flex justify-between px-3 py-1.5 bg-ink-50 border-b border-ink-200"><span className="text-[10px] text-ink-500 font-sans">Draw</span><button type="button" onClick={() => { const c = sigRef.current; if (c) c.getContext("2d")?.clearRect(0, 0, c.width, c.height); }} className="text-[10px] text-brand-600 font-sans">Clear</button></div><canvas ref={sigRef} className="w-full h-16 bg-white cursor-crosshair" /></div>}
                        {f.type === "hidden" && <div className="text-[10px] italic text-ink-500 bg-ink-50 rounded px-2 py-1.5 mb-2 font-sans">Hidden: {f.defaultValue}</div>}
                        {f.type === "terms_checkbox" && (
                          <label className="flex items-start gap-2 mb-2 cursor-pointer">
                            <input type="checkbox" onChange={(e) => setTextValues(v => ({ ...v, [f.name]: e.target.checked ? "yes" : "" }))} className="mt-0.5 accent-[var(--brand-500)] flex-shrink-0" />
                            <span className="text-[11px] text-ink-500 font-sans leading-relaxed" dangerouslySetInnerHTML={{ __html: f.settings.termsText ?? "I agree to the Terms and Conditions" }} />
                          </label>
                        )}
                        {!LAYOUT_TYPES.includes(f.type) && !["textarea", "select", "multiselect", "radio", "checkbox", "rating", "slider", "file", "signature", "hidden", "terms_checkbox"].includes(f.type) && (
                          <input type={({ "email": "email", "phone": "tel", "number": "number", "url": "url", "password": "password", "date": "date", "time": "time", "datetime": "datetime-local", "date_range": "date" } as any)[f.type] ?? "text"} className={`${ic} ${eClass}`} placeholder={mlGet(f.placeholder, lang)} value={textValues[f.name] ?? ""} onChange={e => setTextValues(v => ({ ...v, [f.name]: e.target.value }))} />
                        )}
                        {error && <p className="text-[10px] text-red-500 mt-1 font-sans">{error}</p>}
                      </div>
                    );
                  };

                  for (let i = 0; i < fields.length; i++) {
                    const f1 = fields[i];
                    if (f1.width === "half" && i + 1 < fields.length && fields[i + 1].width === "half") {
                      const f2 = fields[i + 1];
                      elements.push(
                        <div key={i} className="grid grid-cols-2 gap-4">
                          {renderField(f1, `field-${i}`, "")}
                          {renderField(f2, `field-${i + 1}`, "")}
                        </div>
                      );
                      i++;
                    } else {
                      const wrapperClass = f1.width === "half" ? "w-1/2" : "w-full";
                      elements.push(renderField(f1, `field-${i}`, wrapperClass));
                    }
                  }

                  return elements;
                })()}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-3 mt-4">
        {isMultiStepActive && currentStep > 0 && (
          <button onClick={handleBack} disabled={busy} className="px-4 py-2 rounded-lg text-xs font-semibold font-sans border border-ink-200 text-ink-700 bg-white hover:bg-ink-50 transition-colors">
            Back
          </button>
        )}
        {isMultiStepActive && currentStep < steps.length - 1 ? (
          <button onClick={handleNext} className="flex-1 py-2 rounded-lg text-xs font-semibold font-sans text-white" style={{ background: form.custom_styles?.btnColor ?? "#ed5a29" }}>
            Next
          </button>
        ) : (
          <button onClick={submit} disabled={busy} className="flex-1 py-2 rounded-lg text-xs font-semibold font-sans text-white disabled:opacity-70" style={{ background: form.custom_styles?.btnColor ?? "#ed5a29" }}>
            {busy ? mlGet(form.submit_btn_loading_text as any, lang) : mlGet(form.submit_btn_text as any, lang)}
          </button>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Approval Modal
// ─────────────────────────────────────────────────────────────────────────────

const ApprovalModal: React.FC<{
  action: "approve" | "reject";
  submission: FormSubmission;
  requireNote: boolean;
  onConfirm: (note: string) => Promise<void>;
  onClose: () => void;
}> = ({ action, submission, requireNote, onConfirm, onClose }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isApprove = action === "approve";

  const handleSubmit = async () => {
    if (requireNote && !note.trim()) { setError("Please enter a note before proceeding."); return; }
    setBusy(true); setError("");
    try { await onConfirm(note.trim()); onClose(); }
    catch (e: any) { setError(e?.message ?? "Something went wrong."); }
    finally { setBusy(false); }
  };

  return createPortal(
    <motion.div
      initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
      animate={{ opacity: 1, backdropFilter: "blur(2px)" }}
      exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className={`flex items-center gap-3 px-6 py-4 border-b ${isApprove ? "bg-green-50 border-green-100" : "bg-red-50 border-red-100"}`}>
          {isApprove
            ? <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            : <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />}
          <div>
            <p className="text-sm font-bold text-ink-900 font-sans">
              {isApprove ? "Approve submission" : "Reject submission"}
            </p>
            <p className="text-[11px] text-ink-500 font-sans">
              #{submission.id} · {submission.customer_email ?? "Anonymous"}
            </p>
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-xs text-ink-500 font-sans mb-4">
            {isApprove
              ? "The submitter will receive an approval email (if configured). This cannot be undone."
              : "The submitter will receive a rejection email (if configured). This cannot be undone."}
          </p>
          <label className="block text-xs font-semibold text-ink-500 mb-1.5 font-sans">
            {requireNote ? "Admin Note *" : "Admin Note (optional)"}
          </label>
          <textarea
            rows={4}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={isApprove ? "e.g. Approved! Your account is now active." : "e.g. Missing required documents. Please resubmit."}
            className="w-full px-3 py-2 text-sm border border-ink-200 rounded-xl bg-white font-sans focus:border-brand-500 outline-none resize-none"
          />
          {error && <p className="mt-2 text-xs text-red-500 font-sans">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ink-200 bg-ink-50/40">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-xs font-medium font-sans border border-ink-200 rounded-xl hover:bg-white text-ink-500 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={busy}
            className={`flex items-center gap-2 px-4 py-2 text-xs font-medium font-sans rounded-xl disabled:opacity-50 text-white ${isApprove ? "bg-green-600 hover:bg-green-700" : "bg-red-500 hover:bg-red-600"}`}>
            {busy
              ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              : isApprove ? <ThumbsUp className="h-3.5 w-3.5" /> : <ThumbsDown className="h-3.5 w-3.5" />}
            {busy ? "Processing…" : isApprove ? "Approve" : "Reject"}
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: FormSubmission["status"] }> = ({ status }) => {
  const map: Record<string, { label: string; cls: string }> = {
    new: { label: "NEW", cls: "bg-brand-500 text-white" },
    read: { label: "READ", cls: "bg-borderGreyColor text-ink-500" },
    pending: { label: "PENDING", cls: "bg-amber-100 text-amber-700" },
    approved: { label: "APPROVED", cls: "bg-green-100 text-green-700" },
    rejected: { label: "REJECTED", cls: "bg-red-100 text-red-500" },
    starred: { label: "STARRED", cls: "bg-yellow-100 text-yellow-700" },
    archived: { label: "ARCHIVED", cls: "bg-gray-100 text-gray-500" },
    spam: { label: "SPAM", cls: "bg-red-50 text-red-400" },
  };
  const cfg = map[status] ?? { label: status.toUpperCase(), cls: "bg-gray-100 text-gray-500" };
  const isPulsing = status === "new" || status === "pending";
  return (
    <motion.span
      className={`inline-block text-[10px] font-bold rounded px-1.5 py-0.5 font-sans ${cfg.cls}`}
      animate={isPulsing ? { opacity: [1, 0.6, 1] } : {}}
      transition={isPulsing ? { repeat: Infinity, duration: 2, ease: "easeInOut" } : {}}
    >
      {cfg.label}
    </motion.span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SelectedFieldSettings (memoized)
// ─────────────────────────────────────────────────────────────────────────────

const SelectedFieldSettings: React.FC<{
  fields: FormField[];
  selectedIdx: number;
  updateField: (idx: number, f: FormField) => void;
  deleteField: (idx: number) => void;
  languages: Language[];
  activeLang: string;
  isMultiStep?: boolean;
}> = React.memo(({ fields, selectedIdx, updateField, deleteField, languages, activeLang, isMultiStep }) => {
  const field = fields[selectedIdx];
  if (!field) return (
    <div className="flex flex-col items-center justify-center h-full text-center p-8">
      <div className="text-3xl mb-3">👈</div>
      <p className="text-sm font-semibold text-ink-900 font-sans mb-1">Select a field</p>
      <p className="text-xs text-ink-500 font-sans">Click any field on the canvas</p>
    </div>
  );
  return (
    <FieldSettingsPanel
      field={field}
      allFields={fields}
      onChange={f => updateField(selectedIdx, f)}
      onDelete={() => deleteField(selectedIdx)}
      languages={languages}
      activeLang={activeLang}
      isMultiStep={isMultiStep}
    />
  );
});
SelectedFieldSettings.displayName = "SelectedFieldSettings";

// ─────────────────────────────────────────────────────────────────────────────
// Layout wrappers
// ─────────────────────────────────────────────────────────────────────────────

const PageWrap: React.FC<{
  children: React.ReactNode;
  id?: string;
  className?: string;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  scrollContainerClassName?: string;
  "data-scrolled"?: boolean;
}> = ({ children, id, className = "", onScroll, scrollContainerClassName = "", ...rest }) => {
  const wrapRef = useRef<HTMLDivElement>(null);

  return (
    <motion.div
      ref={wrapRef}
      key={id}
      initial={{ opacity: 0, scale: 0.98, filter: "blur(4px)" }}
      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
      exit={{ opacity: 0, scale: 0.98, filter: "blur(4px)" }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      onAnimationComplete={() => {
        if (wrapRef.current) {
          wrapRef.current.style.transform = "";
          wrapRef.current.style.filter = "";
        }
      }}
      className={`flex-1 bg-ink-50 overflow-hidden ${className}`}
    >
      <div
        className={`h-full overflow-y-auto px-4 md:px-6 lg:px-8 pt-0 pb-12 ${scrollContainerClassName}`}
        onScroll={onScroll}
        {...rest}
      >
        {children}
      </div>
    </motion.div>
  );
};

const FullWrap: React.FC<{ children: React.ReactNode; id?: string }> = ({ children, id }) => {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  return (
    <motion.div
      key={id}
      initial={{ opacity: 0, scale: 0.98, filter: "blur(4px)" }}
      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
      exit={{ opacity: 0, scale: 0.98, filter: "blur(4px)" }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      className="fixed inset-0 z-40 flex flex-col bg-ink-50 overflow-hidden"
    >
      {children}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Templates
// ─────────────────────────────────────────────────────────────────────────────

interface TemplateField {
  type: string;
  label?: string;
  name?: string;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  settings?: Record<string, any>;
}

const TEMPLATES: {
  id: string; name: string; description: string; icon: React.ElementType;
  color: string; tag: string; fields: TemplateField[];
  defaultName: string; defaultDescription: string;
  /** Pre-applied form-level settings (e.g. registration config) beyond just fields */
  formSettings?: Partial<Form>;
}[] = [
    {
      id: "contact",
      name: "Contact Us",
      description: "Simple contact form for customer inquiries",
      icon: MessageSquare, color: "bg-blue-50 text-blue-600 border-blue-100",
      tag: "Popular",
      defaultName: "Contact Us",
      defaultDescription: "Get in touch with our team for any questions or support.",
      fields: [
        { type: "text", label: "Full Name", name: "full_name", required: true, placeholder: "John Smith" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "you@example.com" },
        { type: "phone", label: "Phone Number", name: "phone", required: false, placeholder: "+1 (555) 000-0000" },
        { type: "select", label: "Subject", name: "subject", required: true, options: ["General Inquiry", "Product Question", "Order Support", "Wholesale", "Other"] },
        { type: "textarea", label: "Message", name: "message", required: true, placeholder: "How can we help?", settings: { rows: 4 } },
      ],
    },
    {
      id: "newsletter",
      name: "Newsletter Signup",
      description: "Collect email subscribers for your mailing list",
      icon: Mail, color: "bg-purple-50 text-purple-600 border-purple-100",
      tag: "Popular",
      defaultName: "Newsletter Signup",
      defaultDescription: "Subscribe to get the latest news, offers and updates.",
      fields: [
        { type: "text", label: "First Name", name: "first_name", required: true, placeholder: "First name" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "your@email.com" },
        { type: "checkbox", label: "Interests", name: "interests", required: false, options: ["New Arrivals", "Sales & Offers", "Tips & Tutorials", "Events"] },
        { type: "heading", label: "Privacy", name: "privacy_heading" },
        { type: "paragraph", label: "We respect your privacy. Unsubscribe at any time.", name: "privacy_text" },
      ],
    },
    {
      id: "feedback",
      name: "Product Feedback",
      description: "Collect ratings and reviews from customers",
      icon: Star, color: "bg-amber-50 text-amber-600 border-amber-100",
      tag: "Popular",
      defaultName: "Product Feedback",
      defaultDescription: "Share your experience with us.",
      fields: [
        { type: "text", label: "Your Name", name: "name", required: false, placeholder: "Optional" },
        { type: "rating", label: "Overall Rating", name: "rating", required: true, settings: { maxStars: 5 } },
        { type: "select", label: "What did you purchase?", name: "product", required: false, options: ["Clothing", "Accessories", "Footwear", "Home & Living", "Beauty"] },
        { type: "radio", label: "Would you recommend us?", name: "recommend", required: true, options: ["Definitely yes", "Probably yes", "Not sure", "Probably not", "Definitely not"] },
        { type: "textarea", label: "Additional Comments", name: "comments", required: false, placeholder: "Tell us more...", settings: { rows: 3 } },
      ],
    },
    {
      id: "event-registration",
      name: "Event Registration",
      description: "Register attendees for events or webinars",
      icon: UserCheck, color: "bg-green-50 text-green-600 border-green-100",
      tag: "",
      defaultName: "Event Registration",
      defaultDescription: "Register for our upcoming event.",
      fields: [
        { type: "text", label: "Full Name", name: "full_name", required: true, placeholder: "Your full name" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "your@email.com" },
        { type: "phone", label: "Phone", name: "phone", required: false, placeholder: "+1 (555) 000-0000" },
        { type: "select", label: "Ticket Type", name: "ticket_type", required: true, options: ["General Admission", "VIP", "Student / Youth", "Speaker / Press"] },
        { type: "number", label: "Number of Attendees", name: "attendees", required: true, placeholder: "1" },
        { type: "select", label: "Dietary Requirements", name: "dietary", required: false, options: ["None", "Vegetarian", "Vegan", "Gluten-Free", "Halal", "Kosher"] },
        { type: "textarea", label: "Special Requests", name: "special_requests", required: false, placeholder: "Any accessibility needs or requests?", settings: { rows: 2 } },
      ],
    },
    {
      id: "job-application",
      name: "Job Application",
      description: "Collect job applications with CV upload",
      icon: Briefcase, color: "bg-slate-50 text-slate-600 border-slate-100",
      tag: "",
      defaultName: "Job Application",
      defaultDescription: "Apply for a position at our company.",
      fields: [
        { type: "heading", label: "Personal Information", name: "personal_heading" },
        { type: "text", label: "Full Name", name: "full_name", required: true, placeholder: "John Smith" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "your@email.com" },
        { type: "phone", label: "Phone", name: "phone", required: true, placeholder: "+1 (555) 000-0000" },
        { type: "heading", label: "Role & Experience", name: "role_heading" },
        { type: "select", label: "Position Applied For", name: "position", required: true, options: ["Customer Service", "Sales Associate", "Marketing", "Operations", "IT / Tech", "Management"] },
        { type: "select", label: "Years of Experience", name: "experience", required: true, options: ["Less than 1 year", "1–2 years", "3–5 years", "5–10 years", "10+ years"] },
        { type: "file", label: "Upload CV / Resume", name: "cv", required: true, settings: { accept: ".pdf,.doc,.docx", multiple: false } },
        { type: "textarea", label: "Cover Letter", name: "cover_letter", required: false, placeholder: "Tell us why you'd be a great fit...", settings: { rows: 5 } },
      ],
    },
    {
      id: "order-inquiry",
      name: "Order Inquiry",
      description: "Let customers ask about their orders",
      icon: ShoppingCart, color: "bg-orange-50 text-orange-600 border-orange-100",
      tag: "",
      defaultName: "Order Inquiry",
      defaultDescription: "Have a question about your order? We're here to help.",
      fields: [
        { type: "text", label: "Full Name", name: "full_name", required: true, placeholder: "Your name" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "Order confirmation email" },
        { type: "text", label: "Order Number", name: "order_number", required: true, placeholder: "#1001" },
        { type: "radio", label: "Inquiry Type", name: "inquiry_type", required: true, options: ["Where is my order?", "Wrong item received", "Item damaged", "Request return", "Other"] },
        { type: "textarea", label: "Details", name: "details", required: false, placeholder: "Please describe your issue...", settings: { rows: 3 } },
        { type: "file", label: "Attach Photo (if applicable)", name: "photo", required: false, settings: { accept: "image/*", multiple: false } },
      ],
    },
    {
      id: "survey",
      name: "Customer Survey",
      description: "NPS and satisfaction survey",
      icon: ClipboardList, color: "bg-teal-50 text-teal-600 border-teal-100",
      tag: "NPS",
      defaultName: "Customer Satisfaction Survey",
      defaultDescription: "Help us improve by sharing your feedback.",
      fields: [
        { type: "rating", label: "How would you rate your overall experience?", name: "overall_rating", required: true, settings: { maxStars: 5 } },
        { type: "radio", label: "How likely are you to recommend us?", name: "nps", required: true, options: ["Very likely", "Likely", "Neutral", "Unlikely", "Very unlikely"] },
        { type: "checkbox", label: "What did you enjoy most?", name: "enjoyed", required: false, options: ["Product quality", "Customer service", "Shipping speed", "Price", "Website experience"] },
        { type: "radio", label: "How did you hear about us?", name: "source", required: false, options: ["Search engine", "Social media", "Friend / Referral", "Advertisement", "Returning customer"] },
        { type: "textarea", label: "Any suggestions for improvement?", name: "suggestions", required: false, placeholder: "Your feedback helps us get better!", settings: { rows: 3 } },
      ],
    },
    {
      id: "support",
      name: "Support Ticket",
      description: "Technical support request form",
      icon: HelpCircle, color: "bg-cyan-50 text-cyan-600 border-cyan-100",
      tag: "",
      defaultName: "Support Request",
      defaultDescription: "Submit a support ticket and we'll get back to you within 24 hours.",
      fields: [
        { type: "text", label: "Full Name", name: "full_name", required: true, placeholder: "Your name" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "your@email.com" },
        { type: "select", label: "Issue Category", name: "category", required: true, options: ["Account / Login", "Billing", "Product / Order", "Technical Issue", "Feature Request", "Other"] },
        { type: "select", label: "Priority", name: "priority", required: true, options: ["Low — General question", "Medium — Something isn't working", "High — Blocking my workflow", "Critical — Major outage"] },
        { type: "text", label: "Subject", name: "subject", required: true, placeholder: "Brief description of the issue" },
        { type: "textarea", label: "Detailed Description", name: "description", required: true, placeholder: "Steps to reproduce, expected vs actual behavior...", settings: { rows: 5 } },
        { type: "file", label: "Attachments (screenshots, logs)", name: "attachments", required: false, settings: { accept: "image/*,.pdf,.txt,.log", multiple: true, maxFiles: 5 } },
      ],
    },
    {
      id: "customer-registration",
      name: "Customer Registration",
      description: "Register visitors as real Shopify customers, with approval",
      icon: UserPlus, color: "bg-emerald-50 text-emerald-600 border-emerald-100",
      tag: "Registration",
      defaultName: "Customer Registration",
      defaultDescription: "Create an account to get started.",
      fields: [
        { type: "text", label: "First Name", name: "first_name", required: true, placeholder: "Jane" },
        { type: "text", label: "Last Name", name: "last_name", required: true, placeholder: "Doe" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "you@example.com" },
        { type: "phone", label: "Phone Number", name: "phone", required: false, placeholder: "+1 (555) 000-0000", settings: { dependsOnCountry: "country" } },
        { type: "text", label: "Company (optional)", name: "company", required: false, placeholder: "Optional" },
        { type: "text", label: "Address Line 1", name: "address1", required: false, placeholder: "Street address" },
        { type: "text", label: "City", name: "city", required: false, placeholder: "City" },
        { type: "select", label: "Country", name: "country", required: false, options: COUNTRY_NAMES },
        { type: "select", label: "State / Province", name: "province", required: false, options: [], settings: { dependsOnCountry: "country" } },
        { type: "text", label: "ZIP / Postal Code", name: "zip", required: false, placeholder: "ZIP" },
      ],
      formSettings: {
        is_registration_form: true,
        registration_approval_mode: "manual",
        registration_tags: ["registered-customer"],
        registration_field_mapping: {
          email: "email", first_name: "first_name", last_name: "last_name",
          phone: "phone", company: "company", address1: "address1", city: "city",
          country: "country", province: "province", zip: "zip",
        },
      },
    },
    {
      id: "wholesale-registration",
      name: "Wholesale / B2B Registration",
      description: "Vet and onboard wholesale buyers as tagged Shopify customers",
      icon: Building2, color: "bg-indigo-50 text-indigo-600 border-indigo-100",
      tag: "Registration",
      defaultName: "Wholesale Registration",
      defaultDescription: "Apply for a wholesale account with us.",
      fields: [
        { type: "text", label: "Business / Company Name", name: "company", required: true, placeholder: "Acme Co." },
        { type: "text", label: "First Name", name: "first_name", required: true, placeholder: "Jane" },
        { type: "text", label: "Last Name", name: "last_name", required: true, placeholder: "Doe" },
        { type: "email", label: "Email Address", name: "email", required: true, placeholder: "you@business.com" },
        { type: "phone", label: "Phone Number", name: "phone", required: true, placeholder: "+1 (555) 000-0000", settings: { dependsOnCountry: "country" } },
        { type: "text", label: "Address Line 1", name: "address1", required: true, placeholder: "Street address" },
        { type: "text", label: "Address Line 2", name: "address2", required: false, placeholder: "Suite, unit, etc. (optional)" },
        { type: "text", label: "City", name: "city", required: true, placeholder: "City" },
        { type: "select", label: "Country", name: "country", required: true, options: COUNTRY_NAMES },
        { type: "select", label: "State / Province", name: "province", required: false, options: [], settings: { dependsOnCountry: "country" } },
        { type: "text", label: "ZIP / Postal Code", name: "zip", required: true, placeholder: "ZIP" },
        { type: "text", label: "Tax ID / Business Registration Number", name: "tax_id", required: false, placeholder: "e.g. EIN, VAT number" },
        { type: "select", label: "Estimated Monthly Order Volume", name: "order_volume", required: false, options: ["Under $1,000", "$1,000 – $5,000", "$5,000 – $20,000", "$20,000+"] },
      ],
      formSettings: {
        is_registration_form: true,
        registration_approval_mode: "manual",
        registration_tags: ["wholesale", "b2b"],
        registration_field_mapping: {
          email: "email", first_name: "first_name", last_name: "last_name", phone: "phone",
          address1: "address1", address2: "address2", city: "city", province: "province",
          country: "country", zip: "zip", company: "company",
        },
        registration_metafields: [
          { namespace: "custom", key: "tax_id", type: "single_line_text_field", form_field: "tax_id" },
        ],
      },
    },
  ];

function applyTemplate(
  template: { fields: TemplateField[] } & Record<string, any>,
  langs: string[] = ["en"],
): FormField[] {
  return template.fields.map((f, i) => {
    const base = makeField(f.type as FieldType, langs);
    const label: MultiLangValue = f.label ? buildMultiLangLabel(f.label, langs) : base.label;
    const placeholder: MultiLangValue = f.placeholder ? buildMultiLangPlaceholder(f.placeholder, langs) : (base.placeholder ?? Object.fromEntries(langs.map(l => [l, ""])));
    const helpText: MultiLangValue = f.helpText ? buildMultiLangPlaceholder(f.helpText, langs) : (base.helpText ?? Object.fromEntries(langs.map(l => [l, ""])));
    const options: FieldOptionML[] = (f.options ?? []).map(o =>
      ({ label: buildMultiLangOption(typeof o === 'string' ? o : (o as any).label?.en ?? String(o), langs), selected: false })
    );
    return {
      ...base, label, placeholder, helpText,
      name: f.name ?? base.name,
      required: f.required ?? base.required,
      _id: makeUid(),
      sort_order: i,
      options: f.settings?.dependsOnCountry ? [] : (options.length ? options : base.options),
      settings: { ...base.settings, ...(f.settings ?? {}) },
    } as FormField;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const ActionIconButton = ({
  icon: Icon,
  title,
  description,
  color,
  align,
  onClick,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  color: string;
  align: "left" | "center" | "right";
  onClick: (e: React.MouseEvent) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const wrapperClass =
    align === "left" ? "left-0" :
      align === "right" ? "right-0" :
        "left-1/2 -translate-x-1/2";

  const arrowClass =
    align === "left" ? "left-4" :
      align === "right" ? "right-4" :
        "left-1/2 -translate-x-1/2";

  const textColor = color.split(' ').find(c => c.startsWith('text-')) || 'text-gray-500';
  const badgeBg = textColor.replace('text-', 'bg-').replace(/-\d+$/, '-50');

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.button
        whileHover={{ scale: 1.08, y: -2 }}
        whileTap={{ scale: 0.92 }}
        onClick={(e) => {
          e.stopPropagation();
          onClick(e as any);
        }}
        className={`w-full h-8 flex items-center justify-center rounded-lg border transition-all shadow-sm hover:shadow-md ${color}`}
      >
        <Icon className="h-3.5 w-3.5" />
      </motion.button>

      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.98, transition: { duration: 0.12, ease: "easeIn" } }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            style={{ transformOrigin: "bottom" }}
            className={`absolute bottom-full mb-2 z-[100] pointer-events-none w-48 ${wrapperClass}`}
          >
            <div className="relative bg-white border border-gray-100 rounded-xl shadow-lg shadow-black/5 p-3">
              <div className="flex items-start gap-2.5">
                <div className={`flex-shrink-0 p-1 rounded-md ${badgeBg} ${textColor}`}>
                  <Icon className="h-3 w-3" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-gray-900 font-sans leading-none">{title}</p>
                  <p className="text-[10px] text-gray-500 font-sans leading-snug mt-1">{description}</p>
                </div>
              </div>
            </div>
            <div
              className={`absolute -bottom-1 w-2.5 h-2.5 bg-white border-b border-r border-gray-100 rotate-45 ${arrowClass}`}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
export default function FormBuilderApp() {
  const { isLoading, isAuthenticated } = useAuth();

  if (isLoading) return (
    <div className="flex items-center justify-center h-screen bg-ink-50">
      <RefreshCw className="h-6 w-6 animate-spin text-brand-600" />
    </div>
  );

  if (!isAuthenticated) return (
    <div className="flex items-center justify-center h-screen bg-ink-50">
      <p className="text-sm text-ink-500 font-sans">Authenticating with Shopify…</p>
    </div>
  );

  return <FormBuilderContent />;
}

function FormBuilderContent() {
  const { shop } = useAuth();
  const navigate = useNavigate();

  // Extension uid + block handle from extensions/form-selector — used to deep
  // link straight into the theme editor with the form block ready to add,
  // instead of making merchants navigate Theme Editor -> Apps manually.
  const THEME_BLOCK_ID = "71233d68-a875-3a22-b7ca-1e5919ecde798e821db5/form-embed";
  const openThemeEditorWithBlock = () => {
    if (!shop) return;
    const url = `https://${shop}/admin/themes/current/editor?template=index&addAppBlockId=${THEME_BLOCK_ID}&target=newAppsSection`;
    window.open(url, "_blank", "noopener");
  };

  const [view, setView] = useState<View>("list");
  const [wizardStep, setWizardStep] = useState<WizardStep>("name");
  const [forms, setForms] = useState<Form[]>([]);
  // Store-level plan flag, captured from the forms-list response — a brand-new
  // form has no id yet to learn this from its own show() response, so a new
  // draft is stamped with this the moment it's created (see newForm/DEFAULT_FORM use).
  const [fileUploadLocked, setFileUploadLocked] = useState(false);
  const [activeForm, setActiveForm] = useState<Form | null>(null);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [expandedSubId, setExpandedSubId] = useState<number | null>(null);
  const [analytics, setAnalytics] = useState<FormAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [rightPanel, setRightPanel] = useState<RightPanel>("field");
  const [settingsNav, setSettingsNav] = useState<SettingsNav>("general");
  const [settingsSource, setSettingsSource] = useState<"builder" | "list" | null>(null);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  const [searchSub, setSearchSub] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [subPage, setSubPage] = useState(1);
  const [subTotalPages, setSubTotalPages] = useState(1);
  const [subTotal, setSubTotal] = useState(0);
  const SUB_PER_PAGE = 20;
  const [unread, setUnread] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [palQ, setPalQ] = useState("");
  const [draftName, setDraftName] = useState<MultiLangValue>({});
  const [draftDesc, setDraftDesc] = useState<MultiLangValue>({});
  const [nameErr, setNameErr] = useState("");
  const [previewLanguage, setPreviewLanguage] = useState("en");
  const [storeLanguage, setStoreLanguage] = useState<StoreLanguageConfig | null>(null);
  // Plan's max_forms limit (regular + registration forms combined). null =
  // not loaded yet, -1 = unlimited — same sentinel convention as the backend.
  const [maxForms, setMaxForms] = useState<number | null>(null);
  const [approvalModal, setApprovalModal] = useState<{
    action: "approve" | "reject";
    submission: FormSubmission;
  } | null>(null);

  const [leftDrawerOpen, setLeftDrawerOpen] = useState(false);
  const [rightDrawerOpen, setRightDrawerOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);
  const scrolledRef = useRef(false);
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    const isCurrentlyScrolled = scrollTop > 8;
    if (isCurrentlyScrolled !== scrolledRef.current) {
      scrolledRef.current = isCurrentlyScrolled;
      setIsScrolled(isCurrentlyScrolled);
    }
  }, []);

  useEffect(() => {
    if (ok || err) {
      const timer = setTimeout(() => { setOk(""); setErr(""); }, 3000);
      return () => clearTimeout(timer);
    }
  }, [ok, err]);

  const splitLayoutContainerRef = useRef<HTMLDivElement>(null);
  const {
    splitLeftWidth,
    splitRightWidth,
    canvasTargetWidth: canvasWidth,
    dragging,
    showPulse,
    leftElRef: splitLeftRef,
    rightElRef: splitRightRef,
    centerColRef: centerColumnRef,
    canvasCardRef,
    canvasBadgeRef,
    onPointerDown,
    resetSplit,
    updateCanvasWidth
  } = useThreeColumnLayout(splitLayoutContainerRef, {
    defaultLeft: 380,
    defaultRight: 300,
    minLeft: 260,
    minCenter: 380,
    minRight: 260
  });

  const isCanvasDragging = dragging === 'canvas-left' || dragging === 'canvas-right';
  const showCanvasPulse = showPulse;
  const splitDragging = dragging;
  const splitOnPointerDown = onPointerDown;
  const splitReset = resetSplit;

  const languages: Language[] = useMemo(() => {
    if (storeLanguage?.available?.length) return storeLanguage.available;
    if (storeLanguage?.default) {
      const f = DEFAULT_LANGUAGES.find(l => l.code === storeLanguage.default);
      return f ? [f] : DEFAULT_LANGUAGES;
    }
    return DEFAULT_LANGUAGES;
  }, [storeLanguage]);

  // Hide the app shell/sidebar for full-screen editing flows so they get max width
  useHideChrome(view === "builder" || view === "wizard-name" || view === "template-picker" || view === "settings");

  useEffect(() => {
    setLoading(true);
    fetchForms()
      .then(d => { setForms(d.forms); setFileUploadLocked(!!d.file_upload_locked); })
      .catch(() => { })
      .finally(() => setLoading(false));

    getStoreLanguages()
      .then(({ data }) => {
        if (data?.status !== "success" || !data.language) return;
        const available: Language[] = (data.language.available ?? []).map((l: any) => ({
          code: l.code,
          label: l.label,
          native_name: l.native_name,
          flag: l.flag,
          dir: getDirection(l.code),
        }));
        const cfg: StoreLanguageConfig = {
          default: data.language.default ?? "en",
          selected: data.language.selected ?? data.language.default ?? "en",
          available,
        };
        setStoreLanguage(cfg);
        setPreviewLanguage(cfg.selected || cfg.default || "en");
      })
      .catch(() => { });

    getStoreInfo()
      .then(({ data }) => {
        const limit = data?.store?.plan_limits?.max_forms;
        setMaxForms(typeof limit === "number" ? limit : -1);
      })
      .catch(() => { });
  }, []);

  // Same convention as the backend's storeFormCountExceeded(): -1 (or not
  // yet loaded) means unlimited/unknown, never block on it.
  const formLimitReached = maxForms !== null && maxForms >= 0 && forms.length >= maxForms;

  const openTemplatePicker = () => {
    if (formLimitReached) { navigate("/plans"); return; }
    setActiveForm(null); setDraftName({}); setDraftDesc({}); setNameErr(""); setView("template-picker");
  };

  const selectTemplate = (template: typeof TEMPLATES[0] | null) => {
    const langCodes = languages.map(l => l.code);
    // Seed every store language with the same starting text so switching the
    // language selector shows something sensible instead of blank — the
    // merchant can then override any language right here, or later via
    // Settings > General.
    setDraftName(Object.fromEntries(langCodes.map(c => [c, template?.defaultName ?? ""])));
    setDraftDesc(Object.fromEntries(langCodes.map(c => [c, template?.defaultDescription ?? ""])));
    setNameErr("");
    (window as any).__pendingTemplate = template;
    setView("wizard-name");
  };

  const confirmName = () => {
    const hasName = Object.values(draftName).some(v => v && v.trim());
    if (!hasName) { setNameErr("Please give your form a name."); return; }
    const template: typeof TEMPLATES[0] | null = (window as any).__pendingTemplate ?? null;
    const langCodes = languages.map(l => l.code);
    const primaryLang = langCodes[0] ?? "en";

    if (activeForm?.id) {
      // draftName/draftDesc were seeded from the full existing multi-lang
      // objects (see the Edit Info button below), so they already carry
      // every language's prior value — any edits made via the language
      // selector on this screen are already reflected in them.
      setActiveForm(prev => prev ? { ...prev, name: draftName, description: draftDesc } : prev);
    } else {
      const fields = template ? applyTemplate(template, langCodes) : [];
      const fallbackName = draftName[primaryLang]?.trim() || Object.values(draftName).find(v => v && v.trim()) || "";
      const fallbackDesc = draftDesc[primaryLang]?.trim() || Object.values(draftDesc).find(v => v && v.trim()) || "";
      const name = Object.fromEntries(langCodes.map(c => [c, draftName[c]?.trim() || fallbackName]));
      const description = Object.fromEntries(langCodes.map(c => [c, draftDesc[c]?.trim() || fallbackDesc]));
      setActiveForm({ ...DEFAULT_FORM, name, description, fields, created_from_scratch: template === null, ...(template?.formSettings ?? {}), file_upload_locked: fileUploadLocked });
    }

    (window as any).__pendingTemplate = null;
    setSelectedIdx(-1); setRightPanel("field"); setWizardStep("build"); setView("builder"); setOk(""); setErr("");
  };

  const openBuilder = async (form: Form) => {
    try {
      const f = await fetchForm(form.id!);
      const mergedF = {
        ...f,
        created_from_scratch: f.created_from_scratch ?? form.created_from_scratch,
        is_registration_form: f.is_registration_form ?? form.is_registration_form
      };
      setActiveForm({ ...mergedF, fields: mergedF.fields.map(x => ({ ...x, _id: x._id ?? makeUid() })) });
    }
    catch { setActiveForm(form); }
    setSelectedIdx(-1); setRightPanel("field"); setWizardStep("build"); setView("builder");
  };

  const openSubmissions = async (form: Form) => {
    setActiveForm(form); setView("submissions");
    setSearchSub(""); setStatusFilter(""); setSubPage(1);
    setLoadingSubmissions(true);
    try {
      const d = await fetchSubmissions(form.id!, { page: 1, per_page: SUB_PER_PAGE });
      setSubmissions(d.submissions); setUnread(d.unread); setPendingTotal(d.pending); setSubTotalPages(d.pages); setSubTotal(d.total);
    } catch { }
    finally { setLoadingSubmissions(false); }
  };

  // Re-fetch whenever the status filter or page changes; search is debounced separately.
  useEffect(() => {
    if (view !== "submissions" || !activeForm) return;
    setLoadingSubmissions(true);
    fetchSubmissions(activeForm.id!, { page: subPage, per_page: SUB_PER_PAGE, status: statusFilter, search: searchSub })
      .then(d => { setSubmissions(d.submissions); setUnread(d.unread); setPendingTotal(d.pending); setSubTotalPages(d.pages); setSubTotal(d.total); })
      .catch(() => { })
      .finally(() => setLoadingSubmissions(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subPage, statusFilter]);

  // Search box is debounced and always resets back to page 1.
  useEffect(() => {
    if (view !== "submissions" || !activeForm) return;
    const t = setTimeout(() => {
      if (subPage !== 1) { setSubPage(1); return; } // triggers the effect above via subPage change
      setLoadingSubmissions(true);
      fetchSubmissions(activeForm.id!, { page: 1, per_page: SUB_PER_PAGE, status: statusFilter, search: searchSub })
        .then(d => { setSubmissions(d.submissions); setUnread(d.unread); setPendingTotal(d.pending); setSubTotalPages(d.pages); setSubTotal(d.total); })
        .catch(() => { })
        .finally(() => setLoadingSubmissions(false));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSub]);

  const openAnalytics = async (form: Form) => {
    setActiveForm(form); setView("analytics");
    try { setAnalytics(await fetchAnalytics(form.id!)); } catch { }
  };

  const openSettings = async (form: Form) => {
    try {
      const f = await fetchForm(form.id!);
      const mergedF = {
        ...f,
        created_from_scratch: f.created_from_scratch ?? form.created_from_scratch,
        is_registration_form: f.is_registration_form ?? form.is_registration_form
      };
      setActiveForm({ ...mergedF, fields: mergedF.fields.map(x => ({ ...x, _id: x._id ?? makeUid() })) });
    }
    catch { setActiveForm(form); }
    setSettingsNav("general");
    setSettingsSource("list");
    setView("settings");
  };

  const addField = useCallback((type: FieldType) => {
    if (type === 'file' && activeForm?.file_upload_locked) {
      navigate('/plans');
      return;
    }
    const langCodes = languages.map(l => l.code);
    setActiveForm(prev => {
      if (!prev) return null;
      const f = { ...makeField(type, langCodes), sort_order: prev.fields.length };
      return { ...prev, fields: [...prev.fields, f] };
    });
    setSelectedIdx(-1); setRightPanel("field");
  }, [languages, activeForm?.file_upload_locked, navigate]);

  const updateField = useCallback((idx: number, field: FormField) => {
    setActiveForm(p => { if (!p) return null; const fs = [...p.fields]; fs[idx] = field; return { ...p, fields: fs }; });
  }, []);

  const deleteField = useCallback((idx: number) => {
    setActiveForm(p => p ? { ...p, fields: p.fields.filter((_, i) => i !== idx) } : null);
    setSelectedIdx(-1);
  }, []);

  const duplicateField = useCallback((idx: number) => {
    setActiveForm(p => {
      if (!p) return null;
      const clone = { ...JSON.parse(JSON.stringify(p.fields[idx])), _id: makeUid() };
      const fs = [...p.fields]; fs.splice(idx + 1, 0, clone);
      return { ...p, fields: fs.map((f, i) => ({ ...f, sort_order: i })) };
    });
    setSelectedIdx(idx + 1);
  }, []);

  const moveField = (from: number, to: number) => {
    setActiveForm(p => {
      if (!p) return null;
      const fs = [...p.fields]; const [m] = fs.splice(from, 1); fs.splice(to, 0, m);
      return { ...p, fields: fs.map((f, i) => ({ ...f, sort_order: i })) };
    });
    setSelectedIdx(to);
  };

  const onAddFieldAtIndex = useCallback((type: string, index: number) => {
    if (type === 'file' && activeForm?.file_upload_locked) {
      navigate('/plans');
      return;
    }
    const langCodes = languages.map(l => l.code);
    setActiveForm(prev => {
      if (!prev) return null;
      const f = makeField(type as FieldType, langCodes);
      const fs = [...prev.fields];
      fs.splice(index, 0, f);
      return { ...prev, fields: fs.map((fi, i) => ({ ...fi, sort_order: i })) };
    });
    setSelectedIdx(index);
    setRightPanel("field");
  }, [languages, activeForm?.file_upload_locked, navigate]);

  const { onDragStart, onPaletteDragStart, onDragOver, onCanvasDragOver, onDrop, onCanvasDrop, onDragEnd, overIdx } = useDragDrop(
    activeForm?.fields ?? [],
    useCallback((updater: FormField[] | ((prev: FormField[]) => FormField[])) => {
      setActiveForm(p => {
        if (!p) return null;
        const newFields = typeof updater === 'function' ? updater(p.fields) : updater;
        return { ...p, fields: newFields };
      });
    }, []),
    onAddFieldAtIndex
  );

  const getFormName = (form: Form | null, lang = previewLanguage): string => {
    if (!form) return '';
    const n = form.name;
    if (!n) return '';
    if (typeof n === 'string') {
      try { const p = JSON.parse(n); if (typeof p === 'object') return p[lang] ?? p['en'] ?? Object.values(p)[0] ?? ''; } catch { }
      return n;
    }
    return (n as MultiLangValue)[lang] ?? (n as MultiLangValue)['en'] ?? Object.values(n as MultiLangValue)[0] ?? '';
  };
  // Assigns each field a real 1-indexed `step` number by splitting at every
  // 'divider' field — mirrors the preview's step-splitting above (the "steps"
  // list) exactly, so what the merchant sees in the builder is what actually
  // gets embedded. Without this, every field silently keeps its default
  // step of 1 and both the iframe and direct-embed runtimes render the whole
  // form as a single page (or, for the direct embed, no fields at all).
  const computeMultiStepFields = (fields: FormField[]) => {
    let stepNum = 1;
    let stepHasField = false;
    const stepMetas: { title: string; icon?: string }[] = [{ title: 'Step 1' }];
    const out = fields.map(f => {
      if (f.type === 'divider') {
        if (stepHasField) {
          stepNum++;
          stepHasField = false;
          stepMetas.push({
            title: (f.settings?.sectionLabel as string) || `Step ${stepNum}`,
            icon: (f.settings?.sectionIcon as string) || undefined,
          });
        } else {
          if (f.settings?.sectionLabel) stepMetas[stepMetas.length - 1].title = f.settings.sectionLabel as string;
          if (f.settings?.sectionIcon) stepMetas[stepMetas.length - 1].icon = f.settings.sectionIcon as string;
        }
        return { ...f, step: stepNum };
      }
      stepHasField = true;
      return { ...f, step: stepNum };
    });
    return { fields: out, steps: stepMetas };
  };

  const handleSave = async (andGoTo?: "settings" | "done") => {
    if (!activeForm) return;
    const nameVal = getFormName(activeForm);
    if (!nameVal.trim()) { setErr("Form name is required."); return; }
    setSaving(true); setErr(""); setOk("");
    try {
      let formToSave = activeForm;
      if (activeForm.is_multi_step) {
        const { fields, steps } = computeMultiStepFields(activeForm.fields);
        formToSave = { ...activeForm, fields, steps };
      }
      const saved = await saveForm(formToSave);
      const mergedSaved = {
        ...saved,
        created_from_scratch: saved.created_from_scratch ?? activeForm.created_from_scratch,
        is_registration_form: saved.is_registration_form ?? activeForm.is_registration_form
      };
      // TODO: The durable fix for this is on the BACKEND. The `created_from_scratch` 
      // and `is_registration_form` fields must actually be persisted by the 
      // saveForm/fetchForm API so they survive a full page reload, rather than 
      // just relying on in-memory merging here.
      const withIds = { ...mergedSaved, fields: mergedSaved.fields.map(f => ({ ...f, _id: f._id ?? makeUid() })) };
      setActiveForm(withIds);
      setForms(p => activeForm.id ? p.map(f => f.id === mergedSaved.id ? mergedSaved : f) : [mergedSaved, ...p]);

      if (andGoTo === "settings") {
        setWizardStep("settings");
        setSettingsSource("builder");
        setView("settings");
      } else if (andGoTo === "done") {
        setWizardStep("done");
        setOk("Form saved and published!");
        setView("list");
      } else {
        setOk("Saved!");
        setView("list");
      }
      setTimeout(() => setOk(""), 4000);
    } catch (e: any) { setErr(e.message ?? "Save failed"); } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this form permanently?")) return;
    try { await deleteForm(id); setForms(p => p.filter(f => f.id !== id)); } catch { }
  };

  const handleDuplicate = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const cloned = await duplicateForm(id);
      const withIds = { ...cloned, fields: (cloned.fields ?? []).map((fi: any) => ({ ...fi, _id: fi._id ?? makeUid() })) };
      setForms(p => [withIds, ...p]);
      setOk(`"${mlGet(cloned.name as any, previewLanguage)}" created — click Edit to customise it.`);
      setTimeout(() => setOk(""), 4000);
    } catch (e: any) { setErr(e.message ?? "Duplicate failed"); }
  };

  // Adds the form to the storefront via the Mobimint Form theme app extension
  // block — the only Shopify-compliant integration path (Shopify no longer
  // permits apps to hand merchants a script/Liquid snippet to paste into a
  // Custom HTML/Liquid block; see openThemeEditorWithBlock() above). Copies
  // the slug since that's the one per-form value the merchant still has to
  // paste into the block's "Form Slug" setting themselves.
  const handleCopyEmbed = async (form: Form, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(form.slug ?? "");
      setOk(`Slug "${form.slug}" copied! Opening the theme editor — paste it into the Mobimint Form block's "Form Slug" setting.`);
      setTimeout(() => setOk(""), 5000);
      openThemeEditorWithBlock();
    } catch (err: any) {
      setErr(err.message ?? "Failed to copy slug");
      setTimeout(() => setErr(""), 4000);
    }
  };

  const handleApprove = async (note: string) => {
    if (!approvalModal) return;
    const result = await approveSubmission(approvalModal.submission.id, note);
    // The approval itself always succeeds regardless of what happens below -
    // a Shopify-side sync failure is surfaced as a warning, not a blocker.
    setSubmissions(p => p.map(s =>
      s.id === approvalModal.submission.id ? { ...s, status: "approved" as const, approval_note: note } : s
    ));
    if (result.shopify_sync && !result.shopify_sync.success) {
      setErr(`Approved, but Shopify sync failed: ${result.shopify_sync.message ?? "unknown error"}`);
      setTimeout(() => setErr(""), 8000);
    } else {
      setOk("Submission approved.");
      setTimeout(() => setOk(""), 3000);
    }
  };

  const handleReject = async (note: string) => {
    if (!approvalModal) return;
    await rejectSubmission(approvalModal.submission.id, note);
    setSubmissions(p => p.map(s =>
      s.id === approvalModal.submission.id ? { ...s, status: "rejected" as const, approval_note: note } : s
    ));
  };

  const CATS = ["input", "choice", "advanced", "layout"] as const;
  const CATL = { input: "Input Fields", choice: "Choice Fields", advanced: "Advanced", layout: "Layout" } as const;
  const pal = palQ ? FIELD_PALETTE.filter(f => f.label.toLowerCase().includes(palQ.toLowerCase())) : null;

  const SNAV: { id: SettingsNav; label: string; icon: React.ElementType }[] = [
    { id: "general", label: "General", icon: Settings },
    { id: "style", label: "Style", icon: Palette },
    { id: "security", label: "Security", icon: Shield },
    { id: "notifications", label: "Notifications", icon: Inbox },
    { id: "embed", label: "Embed & Share", icon: Code },
    { id: "integrations", label: "Integrations", icon: Zap },
    { id: "gdpr", label: "GDPR", icon: Lock },
    { id: "approval", label: "Approval", icon: UserCheck },
    { id: "registration", label: "Registration", icon: UserPlus },
  ];

  const visibleSNav = useMemo(() => SNAV.filter(n =>
    n.id !== "registration" || activeForm?.is_registration_form || activeForm?.created_from_scratch
  ), [activeForm?.is_registration_form, activeForm?.created_from_scratch]);

  useEffect(() => {
    if (view === "settings" && settingsNav === "registration" && !activeForm?.is_registration_form && !activeForm?.created_from_scratch) {
      setSettingsNav("general");
    }
  }, [view, settingsNav, activeForm?.is_registration_form, activeForm?.created_from_scratch]);

  const WIZARD_STEPS: { id: WizardStep; label: string }[] = [
    { id: "name", label: "Name" },
    { id: "build", label: "Build" },
    { id: "settings", label: "Settings" },
    { id: "done", label: "Publish" },
  ];

  const WizardBar = () => {
    const stepIdx = WIZARD_STEPS.findIndex(s => s.id === wizardStep);
    return (
      <div className="flex items-center justify-between px-6 py-4 bg-white/80 backdrop-blur-md border-b border-gray-100 flex-shrink-0 z-10 shadow-sm sticky top-0 z-[15] relative">
        <div className="flex items-center gap-4">
          <button onClick={() => {
            if (view === "settings") {
              if (settingsSource === "builder") {
                setWizardStep("build");
                setView("builder");
              } else {
                setView("list");
              }
            } else {
              setView("list");
            }
          }} className="flex items-center justify-center h-8 w-8 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all">
            <ArrowLeft className="h-4 w-4" />
          </button>

          <div className="h-5 w-px bg-gray-200" />

          <div className="flex items-center gap-1.5 mr-4 hidden md:flex">
            {WIZARD_STEPS.map((s, i) => {
              const isActive = s.id === wizardStep;
              const isPast = WIZARD_STEPS.findIndex(step => step.id === wizardStep) > i;
              return (
                <div key={s.id} className="flex items-center gap-1.5">
                  <div className="relative flex items-center justify-center">
                    <motion.div
                      layout
                      className={`h-2.5 w-2.5 rounded-full ${isActive ? 'bg-brand-600' : isPast ? 'bg-brand-400' : 'bg-gray-200'}`}
                      initial={false}
                      animate={{ scale: isActive ? 1.2 : 1 }}
                    />
                    {isActive && (
                      <motion.div
                        layoutId="wizard-glow"
                        className="absolute inset-0 bg-brand-500 rounded-full blur-[2px]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.5 }}
                        transition={{ duration: 0.3 }}
                      />
                    )}
                  </div>
                  {i < WIZARD_STEPS.length - 1 && (
                    <div className="w-4 h-[2px] bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-brand-400"
                        initial={{ width: "0%" }}
                        animate={{ width: isPast ? "100%" : "0%" }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex flex-col">
            <span className="text-sm font-bold text-gray-900 font-sora tracking-tight">
              {activeForm ? mlGet(activeForm.name as any, previewLanguage) : "Untitled Form"}
            </span>
            <span className="text-[10px] text-gray-500 font-sans flex items-center gap-1.5 uppercase font-semibold tracking-wider">
              <span className={`h-1.5 w-1.5 rounded-full ${activeForm?.status === "active" ? "bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.6)]" : "bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.6)]"}`}></span>
              {activeForm?.status === "active" ? "Published" : "Draft"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {languages.length > 1 && (
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 shadow-sm">
              <Globe className="h-3.5 w-3.5 text-gray-400" />
              <LanguageSelector value={previewLanguage} onChange={setPreviewLanguage} languages={languages} size="sm" />
            </div>
          )}



          {view !== "builder" && (
            <Button variant="secondary" size="sm" icon={Save} onClick={() => handleSave()} disabled={saving || !activeForm?.id} className="rounded-xl shadow-sm hover:shadow-md transition-shadow bg-white text-gray-700 border-gray-200">
              {saving ? "Saving..." : "Saved"}
            </Button>
          )}
          {view !== "settings" && (
            <Button variant="primary" size="sm" onClick={() => {
              if (wizardStep === "build") {
                if (activeForm?.id) {
                  setWizardStep("settings");
                  setSettingsSource("builder");
                  setView("settings");
                }
                else handleSave("settings");
              } else if (wizardStep === "settings") {
                handleSave("done");
              }
            }} disabled={saving} className="rounded-xl shadow-md hover:shadow-lg transition-shadow">
              {wizardStep === "settings" ? "Publish" : "Continue to Settings"}
            </Button>
          )}
        </div>
      </div>
    );
  };

  let content: React.ReactNode = null;

  // ── VIEW: FORMS LIST ──────────────────────────────────────────────────────────
  if (view === "list") content = (
    <div className="bg-gray-50 flex flex-col font-sans text-gray-900 selection:bg-brand-100 selection:text-brand-900">
      <PageWrap id="list" onScroll={handleScroll} scrollContainerClassName="group/scroll" data-scrolled={isScrolled}>
        <AnimatePresence>
          {(ok || err) && (
            <motion.div
              initial={{ opacity: 0, y: -40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 25 }}
              className={`fixed top-6 left-1/2 -translate-x-1/2 z-[100] flex flex-col overflow-hidden mb-8 min-w-[300px] rounded-2xl text-sm font-semibold shadow-xl ${ok ? "bg-green-50 text-green-800 border border-green-200/60" : "bg-red-50 text-red-800 border border-red-200/60"}`}
            >
              <div className="flex items-center gap-3 px-5 py-4">
                {ok ? <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" /> : <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />}
                {ok || err}
              </div>
              <motion.div
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: 3, ease: "linear" }}
                className={`h-1 ${ok ? "bg-green-500" : "bg-red-500"}`}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="sticky top-0 z-20 flex flex-col md:flex-row md:items-end justify-between gap-6 -mt-6 pt-6 pb-6 mb-4 bg-gray-50/90 group-data-[scrolled=true]/scroll:bg-gray-50/95 group-data-[scrolled=true]/scroll:shadow-[0_10px_24px_-12px_rgba(15,23,42,0.15)] border-b border-transparent group-data-[scrolled=true]/scroll:border-gray-200/80 transition-all duration-300 ease-out">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-brand-100 flex items-center justify-center flex-shrink-0">
              <FileText className="h-5 w-5 text-brand-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold font-sora text-ink-900">Form Builder</h1>
              <p className="text-xs text-ink-500 font-sans">Create and embed forms anywhere in your store</p>
            </div>
          </div>

          <button
            onClick={openTemplatePicker}
            title={formLimitReached ? `You've used all ${maxForms} forms on your plan — upgrade to create more.` : undefined}
            className={`group flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 w-full md:w-auto ${formLimitReached
              ? "bg-amber-500 text-white hover:bg-amber-600"
              : "bg-brand-600 text-white hover:bg-brand-700"
              }`}
          >
            {formLimitReached
              ? <><Lock className="h-4 w-4" /> Form limit reached — Upgrade</>
              : <><Plus className="h-4 w-4 transition-transform group-hover:rotate-90 duration-300" /> Create New Form</>}
          </button>
        </div>
        {formLimitReached && (
          <p className="relative z-10 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-4 lg:mt-8 mb-4">
            You've used all {maxForms} forms included in your current plan. Upgrade to create more.
          </p>
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm overflow-hidden animate-pulse">
                <div className="flex items-center gap-4 mb-5">
                  <div className="h-12 w-12 rounded-xl bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-2 bg-gray-100 rounded w-full" />
                  <div className="h-2 bg-gray-100 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : forms.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-gray-200 p-16 text-center max-w-2xl mx-auto shadow-sm">
            <div className="h-24 w-24 bg-brand-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
              <Layers className="h-10 w-10 text-brand-500" />
            </div>
            <h3 className="text-xl font-bold font-sora text-gray-900 mb-3">No forms created yet</h3>
            <p className="text-sm text-gray-500 font-sans mb-8 max-w-md mx-auto leading-relaxed">Get started by building a powerful form from scratch, or save time by choosing one of our conversion-optimised templates.</p>
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 17 }} onClick={openTemplatePicker} className="px-8 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 shadow-lg hover:shadow-xl transition-all">Explore Templates</motion.button>
          </motion.div>
        ) : (

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 pt-[10px]">
            {/* Create New Form tile */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              onClick={openTemplatePicker}
              title={formLimitReached ? `You've used all ${maxForms} forms on your plan — upgrade to create more.` : undefined}
              className={`backdrop-blur-sm rounded-2xl border-2 border-dashed p-5 flex flex-col items-center justify-center min-h-[190px] cursor-pointer group transition-all duration-300 ${formLimitReached
                ? "bg-amber-50/60 border-amber-300 hover:border-amber-500 hover:bg-amber-50"
                : "bg-white/60 border-gray-300 hover:border-brand-500 hover:bg-brand-50/50"
                }`}
            >
              <div className={`h-11 w-11 rounded-xl bg-white shadow-sm border flex items-center justify-center mb-3 group-hover:scale-110 group-hover:shadow-md transition-all duration-300 ${formLimitReached ? "border-amber-200" : "border-gray-100"}`}>
                {formLimitReached
                  ? <Lock className="h-5 w-5 text-amber-500" />
                  : <Plus className="h-5 w-5 text-gray-400 group-hover:text-brand-600" />}
              </div>
              <p className={`text-sm font-bold font-sans transition-colors text-center px-2 ${formLimitReached ? "text-amber-700" : "text-gray-600 group-hover:text-brand-700"}`}>
                {formLimitReached ? "Form limit reached — Upgrade" : "Create New Form"}
              </p>
            </motion.div>

            {forms.map((form, i) => (
              <motion.div
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                key={form.id}
                onClick={() => { if (form.locked) { navigate("/plans"); return; } openBuilder(form); }}
                className="relative bg-white rounded-2xl border border-gray-200 p-5 cursor-pointer group
                 hover:border-brand-300 hover:shadow-xl hover:shadow-brand-500/5 hover:-translate-y-1 transition-all duration-300"
              >
                {/* top accent line on hover */}
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-brand-400 to-brand-600 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300 rounded-t-2xl" />

                {/* delete on hover */}
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(form.id!); }}
                  title="Delete form"
                  className="absolute top-3 right-3 h-7 w-7 flex items-center justify-center rounded-lg bg-white shadow-sm border border-red-100 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 transition-all z-10"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>

                {/* header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-brand-50 to-brand-100 flex items-center justify-center flex-shrink-0 border border-brand-200/50 shadow-inner">
                    <FileText className="h-4.5 w-4.5 text-brand-600" />
                  </div>
                  <div className="min-w-0 flex-1 pr-6">
                    <h3 className="text-sm font-bold text-gray-900 font-sora truncate group-hover:text-brand-700 transition-colors">
                      {mlGet(form.name as any, previewLanguage)}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-md font-bold font-sans uppercase tracking-wider ${form.status === "active" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {form.status === "active" ? "Live" : "Draft"}
                      </span>
                      {form.locked && (
                        <span title="Beyond your plan's form limit — upgrade or delete an older form to unlock it" className="flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded-md font-bold font-sans uppercase tracking-wider bg-red-100 text-red-700">
                          <Lock className="h-2.5 w-2.5" /> Locked
                        </span>
                      )}
                      {form.approval_enabled && (
                        <span title="Approval enabled" className="text-purple-500">
                          <UserCheck className="h-3 w-3" />
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* description */}
                {mlGet(form.description as any, previewLanguage) && (
                  <p className="text-xs text-gray-500 font-sans mb-3 line-clamp-2 leading-relaxed min-h-[32px]">
                    {mlGet(form.description as any, previewLanguage)}
                  </p>
                )}

                {/* stats */}
                <div className="flex items-center gap-4 text-[11px] text-gray-500 font-sans mb-4 pb-4 border-b border-gray-100">
                  <span className="flex items-center gap-1"><Inbox className="h-3 w-3 text-gray-400" /> {form.submissions_count ?? 0}</span>
                  <span className="flex items-center gap-1"><Eye className="h-3 w-3 text-gray-400" /> {analytics?.summary?.views ?? 0}</span>
                  {(form.unread_count ?? 0) > 0 && (
                    <span className="ml-auto bg-red-500 text-white rounded-full px-1.5 py-0.5 text-[9px] font-bold animate-pulse">
                      {form.unread_count} new
                    </span>
                  )}
                </div>

                {/* action icons grid */}
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { icon: form.locked ? Lock : Edit2, title: form.locked ? "Locked — Upgrade" : "Edit", description: form.locked ? "Beyond your plan's form limit" : "Modify fields & layout", onClick: () => { if (form.locked) { navigate("/plans"); return; } openBuilder(form); }, color: form.locked ? "text-red-500 bg-red-50 border-red-100 hover:bg-red-100 hover:border-red-300" : "text-blue-600 bg-blue-50 border-blue-100 hover:bg-blue-100 hover:border-blue-300" },
                    { icon: Inbox, title: "Inbox", description: "View submissions", onClick: () => openSubmissions(form), color: "text-purple-600 bg-purple-50 border-purple-100 hover:bg-purple-100 hover:border-purple-300" },
                    { icon: BarChart2, title: "Stats", description: "Form analytics", onClick: () => openAnalytics(form), color: "text-green-600 bg-green-50 border-green-100 hover:bg-green-100 hover:border-green-300" },
                    { icon: Settings, title: form.locked ? "Locked — Upgrade" : "Settings", description: form.locked ? "Beyond your plan's form limit" : "Configure form", onClick: () => { if (form.locked) { navigate("/plans"); return; } openSettings(form); }, color: form.locked ? "text-red-400 bg-red-50 border-red-100 opacity-70" : "text-amber-600 bg-amber-50 border-amber-100 hover:bg-amber-100 hover:border-amber-300" },
                    { icon: Copy, title: form.locked ? "Locked — Upgrade" : "Duplicate", description: form.locked ? "Beyond your plan's form limit" : "Clone this form", onClick: (e: React.MouseEvent) => { if (form.locked) { e.stopPropagation(); navigate("/plans"); return; } handleDuplicate(form.id!, e); }, color: form.locked ? "text-red-400 bg-red-50 border-red-100 opacity-70" : "text-brand-600 bg-brand-50 border-brand-100 hover:bg-brand-100 hover:border-brand-300" },
                  ].map((action, i) => (
                    <ActionIconButton
                      key={action.title}
                      {...action}
                      align={i % 3 === 0 ? "left" : i % 3 === 2 ? "right" : "center"}
                    />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </PageWrap>
    </div>
  );

  // ── VIEW: TEMPLATE PICKER ─────────────────────────────────────────────────────
  if (view === "template-picker") content = (
    <PageWrap id="template-picker" className="bg-gray-50 mx-auto pt-4 pb-8 px-6 min-h-screen">
      <div className="mb-10 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => setView("list")} className="flex items-center justify-center h-10 w-10 bg-white border border-gray-200 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-full transition-all shadow-sm">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold font-sora text-gray-900 tracking-tight">Choose a Template</h1>
            <p className="text-gray-500 font-sans text-sm mt-1">Start with a pre-built layout tailored to your needs, or build from scratch.</p>
          </div>
        </div>
      </div>

      <div onClick={() => selectTemplate(null)} className="flex items-center gap-6 bg-gradient-to-r from-brand-50 to-white border-2 border-dashed border-brand-300 hover:border-brand-500 rounded-3xl p-4 mb-10 cursor-pointer group transition-all duration-300 hover:shadow-xl shadow-sm">
        <div className="h-14 w-14 rounded-2xl bg-brand-600 flex items-center justify-center flex-shrink-0 group-hover:scale-105 group-hover:rotate-3 transition-all duration-300 shadow-lg shadow-brand-500/30">
          <Layers className="h-6 w-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-gray-900 font-sora mb-1 group-hover:text-brand-700 transition-colors">Start from Scratch</h2>
          <p className="text-sm text-gray-500 font-sans max-w-xl">Build your form from a blank canvas. Full control over every field, layout, and style.</p>
        </div>
        <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center shadow-sm border border-gray-100 group-hover:bg-brand-600 group-hover:border-brand-600 transition-colors">
          <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors" />
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <h3 className="text-lg font-bold text-gray-900 font-sora">Pre-Built Templates</h3>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
        {TEMPLATES.map((t, i) => (
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
            key={t.id}
            onClick={() => selectTemplate(t)}
            whileHover={{ y: -4, scale: 1.01, boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(79, 70, 229, 0.1)" }}
            className="relative bg-white rounded-2xl border border-gray-200 p-5 cursor-pointer group flex flex-col h-full overflow-hidden
                 hover:border-brand-300 transition-colors duration-300"
          >
            {/* top accent line on hover */}
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-brand-400 to-brand-600 scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-300" />

            {/* icon + tag */}
            <div className="flex items-start justify-between mb-4">
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm border border-white/50 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 ${t.color}`}>
                <t.icon className="h-6 w-6" />
              </div>
              {t.tag && (
                <span className="text-[9px] font-bold bg-brand-100 text-brand-700 px-2 py-1 rounded-md uppercase tracking-wider flex-shrink-0">
                  {t.tag}
                </span>
              )}
            </div>

            {/* title + desc */}
            <h3 className="text-sm font-bold text-gray-900 font-sora mb-1.5 group-hover:text-brand-700 transition-colors">
              {t.name}
            </h3>
            <p className="text-xs text-gray-500 font-sans mb-4 flex-1 leading-relaxed line-clamp-2 min-h-[32px]">
              {t.description}
            </p>

            {/* field chips - max 3 for compact look */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {t.fields.filter(f => !["heading", "paragraph"].includes(f.type ?? '')).slice(0, 3).map((f, i) => (
                <span key={i} className="text-[10px] font-medium bg-gray-50 border border-gray-100 text-gray-600 px-2 py-0.5 rounded-md font-sans">
                  {f.label}
                </span>
              ))}
              {t.fields.filter(f => !["heading", "paragraph"].includes(f.type ?? '')).length > 3 && (
                <span className="text-[10px] font-medium bg-gray-100 text-gray-500 px-2 py-0.5 rounded-md font-sans">
                  +{t.fields.filter(f => !["heading", "paragraph"].includes(f.type ?? '')).length - 3}
                </span>
              )}
            </div>

            {/* footer */}
            <div className="flex items-center justify-between pt-3 border-t border-gray-100">
              <span className="text-[10px] font-semibold text-gray-400 font-sans">{t.fields.length} fields</span>
              <span className="text-xs font-bold text-brand-600 font-sans flex items-center gap-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                Use <ChevronRight className="h-3.5 w-3.5" />
              </span>
            </div>
          </motion.div>
        ))}
      </div>
    </PageWrap>
  );

  // ── VIEW: WIZARD NAME ─────────────────────────────────────────────────────────
  if (view === "wizard-name") content = (
    <PageWrap id="wizard-name" className="bg-gray-50 min-h-screen flex flex-col items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-2xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-10">
          {WIZARD_STEPS.map((s, i) => (
            <React.Fragment key={s.id}>
              <div className={`flex flex-col items-center gap-2 ${i === 0 ? "opacity-100" : "opacity-40"}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm transition-colors ${i === 0 ? "bg-brand-600 text-white shadow-brand-500/30" : "bg-white text-gray-500 border border-gray-200"}`}>
                  {i + 1}
                </div>
                <span className={`text-xs font-semibold font-sans tracking-wide ${i === 0 ? "text-brand-700" : "text-gray-500"}`}>{s.label}</span>
              </div>
              {i < WIZARD_STEPS.length - 1 && <div className={`w-12 h-0.5 rounded-full mb-6 ${i === 0 ? "bg-brand-200" : "bg-gray-200"}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white rounded-[2rem] border border-gray-100 p-6 md:p-12 shadow-2xl shadow-gray-200/50">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold font-sora text-gray-900 tracking-tight mb-3">Name your form</h2>
            <p className="text-base text-gray-500 font-sans max-w-md mx-auto">Give your form a clear, descriptive name so you and your team can easily identify it later.</p>
          </div>

          {languages.length > 1 && (
            <div className="flex justify-center mb-6">
              <LanguageSelector value={previewLanguage} onChange={setPreviewLanguage} languages={languages} size="sm" />
            </div>
          )}

          <div className="space-y-6 mb-10">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-bold text-gray-700 mb-2 font-sans">
                Form Name <span className="text-red-500">*</span>
                {languages.length > 1 && (
                  <span className="text-[10px] text-brand-600 bg-brand-100 px-1.5 py-0.5 rounded ml-1 font-sans uppercase">{previewLanguage}</span>
                )}
              </label>
              <input autoFocus value={mlGet(draftName, previewLanguage)} onChange={e => { setDraftName(mlSet(mlWrap(draftName, languages[0]?.code ?? 'en'), previewLanguage, e.target.value)); if (e.target.value.trim()) setNameErr(""); }} onKeyDown={e => e.key === "Enter" && confirmName()} placeholder="e.g. Summer Sale Feedback, VIP Waitlist..."
                className={`w-full px-5 py-4 text-base border-2 rounded-2xl bg-gray-50 font-sans focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all shadow-sm ${nameErr ? "border-red-300 focus:border-red-500 focus:ring-red-500/10" : "border-gray-200"}`} />
              {nameErr && <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} className="text-sm font-semibold text-red-500 mt-2 font-sans flex items-center gap-1"><AlertTriangle className="h-4 w-4" />{nameErr}</motion.p>}
            </div>
            <div>
              <label className="flex items-center justify-between text-sm font-bold text-gray-700 mb-2 font-sans">
                <span className="flex items-center gap-1.5">
                  Description <span className="text-gray-400 font-medium text-xs bg-gray-100 px-2 py-0.5 rounded-md">Optional</span>
                  {languages.length > 1 && (
                    <span className="text-[10px] text-brand-600 bg-brand-100 px-1.5 py-0.5 rounded font-sans uppercase">{previewLanguage}</span>
                  )}
                </span>
              </label>
              <textarea value={mlGet(draftDesc, previewLanguage)} onChange={e => setDraftDesc(mlSet(mlWrap(draftDesc, languages[0]?.code ?? 'en'), previewLanguage, e.target.value))} rows={3} placeholder="Brief description to help you remember the purpose of this form..."
                className="w-full px-5 py-4 text-base border-2 border-gray-200 rounded-2xl bg-gray-50 font-sans focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all resize-none shadow-sm" />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-100">
            <button onClick={() => setView("template-picker")} className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-bold text-gray-600 bg-white border-2 border-gray-200 rounded-xl font-sans hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300 transition-all">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 17 }} onClick={confirmName} className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 bg-brand-600 text-white rounded-xl text-sm font-bold font-sans hover:bg-brand-700 hover:shadow-lg hover:shadow-brand-500/30 transition-all">
              Continue to Builder <ChevronRight className="h-4 w-4" />
            </motion.button>
          </div>
        </div>
      </motion.div>
    </PageWrap>
  );

  // ── VIEW: BUILDER ─────────────────────────────────────────────────────────────
  if (view === "builder" && activeForm) content = (
    <FullWrap id="builder">
      <WizardBar />
      <div ref={splitLayoutContainerRef} className="flex flex-1 overflow-hidden min-h-0 bg-gray-100 relative">
        {/* Field Library Column */}
        <div
          ref={splitLeftRef}
          style={{ '--panel-width': `${splitLeftWidth}px` } as React.CSSProperties}
          className={`fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[460px] bg-white border-r border-gray-200 flex flex-col overflow-hidden shadow-2xl transition-transform duration-300 ${leftDrawerOpen ? 'translate-x-0' : '-translate-x-full'} xl:relative xl:translate-x-0 xl:w-[var(--panel-width)] xl:max-w-none xl:shadow-[4px_0_24px_rgba(0,0,0,0.02)] xl:z-10`}
        >
          {/* Drag Handle (Desktop Only) */}
          <div
            className={`hidden xl:flex absolute right-0 top-0 bottom-0 w-3 translate-x-1.5 cursor-col-resize items-center justify-center transition-opacity z-50 group`}
            onPointerDown={(e) => splitOnPointerDown(e, 'left')}
            onDoubleClick={() => splitReset('left')}
            title="Drag to resize"
          >
            <div className={`flex flex-col gap-1 items-center justify-center w-1.5 h-12 rounded-full transition-all ${splitDragging === 'left' ? 'bg-brand-500' : showPulse ? 'animate-pulse bg-brand-400' : 'bg-gray-300/60 group-hover:bg-brand-400'}`}>
              <div className={`w-0.5 h-0.5 rounded-full ${splitDragging === 'left' ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
              <div className={`w-0.5 h-0.5 rounded-full ${splitDragging === 'left' ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
              <div className={`w-0.5 h-0.5 rounded-full ${splitDragging === 'left' ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
            </div>
          </div>
          <div className="p-5 border-b border-gray-100 flex-shrink-0 bg-white relative">
            <button className="xl:hidden absolute top-4 right-4 p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors" onClick={() => setLeftDrawerOpen(false)}><XCircle className="w-5 h-5" /></button>
            <h3 className="text-base font-bold text-gray-900 font-sora tracking-tight mb-4 flex items-center gap-2 pr-8">
              <Layers className="h-5 w-5 text-brand-500" /> Field Library
            </h3>
            <div className="relative group">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-brand-500 transition-colors" />
              <input value={palQ} onChange={e => setPalQ(e.target.value)} placeholder="Search fields…"
                className="w-full pl-10 pr-4 py-3 text-sm border-2 border-gray-100 rounded-xl bg-gray-50 font-sans focus:bg-white focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10 outline-none transition-all" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-5 custom-scrollbar bg-gray-50/50">
            {pal ? (
              <motion.div
                key="search-results"
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
                }}
                className="space-y-2"
              >
                {pal.map(f => {
                  const isLocked = f.type === 'file' && !!activeForm?.file_upload_locked;
                  return (
                  <motion.button
                    key={f.type}
                    draggable={!isLocked}
                    onDragStart={onPaletteDragStart(f.type) as any}
                    onDragEnd={onDragEnd as any}
                    onClick={() => addField(f.type)}
                    title={isLocked ? 'Upgrade your plan to unlock File Upload fields' : undefined}
                    variants={{
                      hidden: { opacity: 0, scale: 0.95, y: 10 },
                      visible: { opacity: 1, scale: 1, y: 0 }
                    }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className={`relative w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left group overflow-hidden ${isLocked ? 'border-amber-200 bg-amber-50/50 hover:border-amber-300' : 'border-gray-200 bg-white hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/10'}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-50/0 to-brand-100/0 group-hover:from-brand-50/40 group-hover:to-brand-100/40 transition-colors duration-300 pointer-events-none" />
                    <span className="relative z-10 text-gray-400 group-hover:text-brand-600 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-brand-100 group-hover:to-brand-50 p-2.5 rounded-xl transition-all duration-300 group-hover:rotate-6 flex-shrink-0 shadow-sm group-hover:shadow">{f.icon}</span>
                    <span className="relative z-10 text-sm font-semibold text-gray-700 font-sans truncate group-hover:text-gray-900">{f.label}</span>
                    {isLocked ? (
                      <span className="relative z-10 ml-auto flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full">
                        <Lock className="h-3 w-3" /> Upgrade
                      </span>
                    ) : (
                      <Plus className="relative z-10 h-4 w-4 text-brand-500 ml-auto opacity-0 scale-50 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300" />
                    )}
                  </motion.button>
                  );
                })}
              </motion.div>
            ) : CATS.map((cat, catIdx) => (
              <motion.div
                key={cat}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: catIdx * 0.1 }}
                className="mb-8"
              >
                <motion.p
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: catIdx * 0.1 + 0.1 }}
                  className="text-[11px] font-bold text-gray-900 uppercase tracking-widest mb-4 font-sans flex items-center gap-3"
                >
                  {CATL[cat]}
                  <span className="h-px flex-1 bg-gradient-to-r from-gray-200 to-transparent mt-0.5"></span>
                </motion.p>
                {/* grid-cols-3 kar diya taaki extra width waste na ho */}
                <motion.div
                  className="flex flex-wrap gap-2.5"
                  initial="hidden"
                  animate="visible"
                  variants={{
                    hidden: { opacity: 0 },
                    visible: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: catIdx * 0.1 + 0.2 } }
                  }}
                >
                  {FIELD_PALETTE.filter(f => f.category === cat).map(f => {
                    const isLocked = f.type === 'file' && !!activeForm?.file_upload_locked;
                    return (
                    <motion.button
                      key={f.type}
                      draggable={!isLocked}
                      onDragStart={onPaletteDragStart(f.type) as any}
                      onDragEnd={onDragEnd as any}
                      onClick={() => addField(f.type)}
                      title={isLocked ? 'Upgrade your plan to unlock File Upload fields' : undefined}
                      variants={{
                        hidden: { opacity: 0, scale: 0.9, y: 10 },
                        visible: { opacity: 1, scale: 1, y: 0 }
                      }}
                      whileHover={{ scale: 1.05, y: -3 }}
                      whileTap={{ scale: 0.95 }}
                      className={`relative flex flex-col items-center justify-center gap-2 p-3 rounded-xl border transition-all text-center group overflow-hidden flex-grow flex-shrink-0 ${isLocked ? 'border-amber-200 bg-amber-50/50 hover:border-amber-300' : 'border-gray-200 bg-white hover:border-brand-300 hover:shadow-lg hover:shadow-brand-500/10'}`}
                      style={{
                        flexBasis: 'calc(33.333% - 10px)',
                        minWidth: '80px',
                        maxWidth: '140px',
                      }}
                    >
                      {/* Hover background glow */}
                      <div className="absolute inset-0 bg-gradient-to-br from-brand-50/0 to-brand-100/0 group-hover:from-brand-50/40 group-hover:to-brand-100/40 transition-colors duration-300 pointer-events-none" />

                      {/* Top-right badge: lock for gated fields, + for everything else */}
                      {isLocked ? (
                        <span className="absolute top-1.5 right-1.5 bg-amber-100 text-amber-600 rounded-full p-0.5 shadow-sm z-10">
                          <Lock className="h-3 w-3" strokeWidth={2.5} />
                        </span>
                      ) : (
                        <span className="absolute top-1.5 right-1.5 bg-brand-100 text-brand-600 rounded-full p-0.5 opacity-0 scale-50 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 shadow-sm z-10">
                          <Plus className="h-3 w-3" strokeWidth={2.5} />
                        </span>
                      )}

                      <span className="relative z-10 text-gray-400 group-hover:text-brand-600 bg-gradient-to-br from-gray-50 to-gray-100 group-hover:from-brand-100 group-hover:to-brand-50 h-9 w-9 flex items-center justify-center rounded-xl shadow-sm group-hover:shadow group-hover:rotate-6 transition-all duration-300">
                        {f.icon}
                      </span>
                      <span className="relative z-10 text-[10px] font-bold text-gray-600 font-sans group-hover:text-gray-900 leading-tight">
                        {f.label}
                      </span>
                    </motion.button>
                    );
                  })}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Canvas Column */}
        <div
          ref={centerColumnRef}
          className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-10 bg-gray-100 min-w-0 flex flex-col items-center relative"
          onDragOver={onCanvasDragOver}
          onDrop={onCanvasDrop}
        >
          {/* Mobile Drawer Toggles */}
          <div className="xl:hidden w-full max-w-3xl flex justify-between mb-4 gap-3">
            <button onClick={() => setLeftDrawerOpen(true)} className="flex-1 flex justify-center items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-brand-200 text-brand-700 text-sm font-bold font-sans hover:bg-brand-50 transition-colors">
              <Layers className="h-4 w-4" /> Add Fields
            </button>
            <button onClick={() => setRightDrawerOpen(true)} className="flex-1 flex justify-center items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-gray-200 text-gray-700 text-sm font-bold font-sans hover:bg-gray-50 transition-colors">
              <Settings className="h-4 w-4" /> Settings
            </button>
          </div>

          {/* Overlays */}
          {leftDrawerOpen && <div className="fixed inset-0 bg-ink-900/20 z-40 xl:hidden backdrop-blur-sm transition-opacity" onClick={() => setLeftDrawerOpen(false)} />}
          {rightDrawerOpen && <div className="fixed inset-0 bg-ink-900/20 z-40 xl:hidden backdrop-blur-sm transition-opacity" onClick={() => setRightDrawerOpen(false)} />}

          {wizardStep === "done" && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-3xl mb-8 bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center gap-4 shadow-sm">
              <div className="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="text-base font-bold text-green-900 font-sans">Your form is live! 🎉</p>
                <p className="text-sm text-green-700 font-sans mt-1">
                  Embed it in the theme editor, then set the slug to: <code className="bg-green-100 text-green-800 px-2 py-0.5 rounded-md font-mono font-bold">{activeForm.slug}</code>
                </p>
                <p className="text-xs text-green-600 font-sans mt-1.5">
                  First form on this store? Also turn on <strong>App Embeds</strong> once in{" "}
                  <button onClick={() => navigate("/settings")} className="underline font-semibold hover:text-green-800">Settings</button> — it's the master switch that makes every form block visible on your live storefront.
                </p>
              </div>
              <button onClick={openThemeEditorWithBlock} className="text-sm font-bold text-white bg-green-600 rounded-xl px-5 py-2.5 hover:bg-green-700 transition-colors shadow-sm">Add to theme</button>
              <button onClick={() => setView("list")} className="text-sm font-bold text-green-700 bg-white border-2 border-green-200 rounded-xl px-5 py-2.5 hover:bg-green-100 transition-colors shadow-sm">View all forms</button>
            </motion.div>
          )}

          {/* Canvas Card Wrapper */}
          <div
            ref={canvasCardRef}
            className="flex flex-col items-center w-full transition-all duration-300"
            style={{
              width: canvasWidth === 'full' ? '100%' : `${canvasWidth}px`,
              maxWidth: canvasWidth === 'full' ? '100%' : `${canvasWidth}px`,
              transition: isCanvasDragging ? 'none' : 'width 0.3s ease, max-width 0.3s ease'
            }}
          >
            {/* Quick-select segmented control */}
            <div className="flex justify-end w-full mb-3">
              <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
                <button onClick={() => updateCanvasWidth(640)} className={`p-1.5 rounded-lg text-gray-500 hover:text-gray-900 transition-colors ${canvasWidth === 640 && !isCanvasDragging ? 'bg-gray-100 text-brand-600 font-bold' : ''}`} title="Mobile (640px)">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                </button>
                <button onClick={() => updateCanvasWidth(768)} className={`p-1.5 rounded-lg text-gray-500 hover:text-gray-900 transition-colors ${canvasWidth === 768 && !isCanvasDragging ? 'bg-gray-100 text-brand-600 font-bold' : ''}`} title="Tablet (768px)">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>
                </button>
                <button onClick={() => updateCanvasWidth(1024)} className={`p-1.5 rounded-lg text-gray-500 hover:text-gray-900 transition-colors ${canvasWidth === 1024 && !isCanvasDragging ? 'bg-gray-100 text-brand-600 font-bold' : ''}`} title="Desktop (1024px)">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line><line x1="12" y1="17" x2="12" y2="21"></line></svg>
                </button>
                <button onClick={() => updateCanvasWidth('full')} className={`p-1.5 rounded-lg text-gray-500 hover:text-gray-900 transition-colors ${canvasWidth === 'full' && !isCanvasDragging ? 'bg-gray-100 text-brand-600 font-bold' : ''}`} title="Full Width">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="18" rx="2" ry="2"></rect></svg>
                </button>
              </div>
            </div>

            {/* The actual resizable card container */}
            <div className="relative w-full group isolate">
              {/* Left Handle */}
              <div
                className={`absolute left-0 top-0 bottom-0 w-3 -translate-x-[calc(100%+8px)] cursor-col-resize flex items-center justify-center opacity-100 transition-opacity z-10 group`}
                onPointerDown={(e) => onPointerDown(e, 'canvas-left')}
                title="Drag to resize"
              >
                <div className={`flex flex-col gap-1 items-center justify-center w-1.5 h-12 rounded-full transition-all ${isCanvasDragging ? 'bg-brand-500' : showCanvasPulse ? 'animate-pulse bg-brand-400' : 'bg-gray-300/60 group-hover:bg-brand-400'}`}>
                  <div className={`w-0.5 h-0.5 rounded-full ${isCanvasDragging ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
                  <div className={`w-0.5 h-0.5 rounded-full ${isCanvasDragging ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
                  <div className={`w-0.5 h-0.5 rounded-full ${isCanvasDragging ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
                </div>
              </div>

              {/* Right Handle */}
              <div
                className={`absolute right-0 top-0 bottom-0 w-3 translate-x-[calc(100%+8px)] cursor-col-resize flex items-center justify-center opacity-100 transition-opacity z-10 group`}
                onPointerDown={(e) => onPointerDown(e, 'canvas-right')}
                title="Drag to resize"
              >
                <div className={`flex flex-col gap-1 items-center justify-center w-1.5 h-12 rounded-full transition-all ${isCanvasDragging ? 'bg-brand-500' : showCanvasPulse ? 'animate-pulse bg-brand-400' : 'bg-gray-300/60 group-hover:bg-brand-400'}`}>
                  <div className={`w-0.5 h-0.5 rounded-full ${isCanvasDragging ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
                  <div className={`w-0.5 h-0.5 rounded-full ${isCanvasDragging ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
                  <div className={`w-0.5 h-0.5 rounded-full ${isCanvasDragging ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
                </div>
              </div>

              {/* Indicator while dragging */}
              {isCanvasDragging && (
                <div
                  ref={canvasBadgeRef}
                  className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-lg pointer-events-none z-50"
                >
                  {typeof canvasWidth === 'number' ? Math.round(canvasWidth) : ''}px
                </div>
              )}

              <div className={`w-full bg-white rounded-[2rem] border border-gray-200 shadow-xl p-8 md:p-12 relative overflow-visible ${isCanvasDragging ? 'shadow-2xl shadow-brand-500/10' : 'shadow-gray-200/50 transition-all duration-300'}`}>
                <div className="flex items-start justify-between mb-8 pb-6 border-b border-gray-100">
                  <div className="pr-6">
                    <h2 className="text-2xl font-bold text-gray-900 font-sora tracking-tight mb-2">{mlGet(activeForm.name as any, previewLanguage)}</h2>
                    {mlGet(activeForm.description as any, previewLanguage) && <p className="text-sm text-gray-500 font-sans leading-relaxed">{mlGet(activeForm.description as any, previewLanguage)}</p>}
                  </div>
                  <button onClick={() => {
                    const primaryLang = languages[0]?.code ?? 'en';
                    setDraftName(mlWrap(activeForm.name as MultiLangValue | string, primaryLang));
                    setDraftDesc(mlWrap(activeForm.description as MultiLangValue | string, primaryLang));
                    setView('wizard-name');
                  }} className="text-xs font-bold text-brand-600 bg-brand-50 hover:bg-brand-100 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors flex-shrink-0">
                    <Edit2 className="h-3 w-3" /> Edit Info
                  </button>
                </div>


                {activeForm.fields.length === 0 ? (
                  <div
                    className="flex flex-col items-center justify-center py-24 my-8 border-2 border-dashed border-gray-300 bg-gray-50 rounded-3xl text-center transition-colors hover:border-brand-400 hover:bg-brand-50/30 group"
                    onDragOver={onCanvasDragOver}
                    onDrop={onCanvasDrop}
                  >
                    <div className="h-20 w-20 bg-white shadow-md border border-gray-200 rounded-[1.5rem] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <Layers className="h-10 w-10 text-gray-300 group-hover:text-brand-500 transition-colors" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 font-sora tracking-tight mb-3">Build your form</h3>
                    <p className="text-sm text-gray-500 font-sans max-w-sm leading-relaxed">Drag and drop fields from the left library, or click any field to add it instantly to your canvas.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(() => {
                      const fields = activeForm.fields;
                      type Section = { divider?: FormField; dividerIdx?: number; sectionFields: FormField[] };
                      const sections: Section[] = [];
                      let current: Section = { sectionFields: [] };

                      fields.forEach((field, globalIdx) => {
                        if (field.type === 'divider') {
                          sections.push(current);
                          current = { divider: field, dividerIdx: globalIdx, sectionFields: [] };
                        } else {
                          current.sectionFields.push(field);
                        }
                      });
                      sections.push(current);

                      const sectionCls = (style?: string) => {
                        switch (style) {
                          case 'bordered': return 'border-2 border-gray-200 bg-gray-50/50 rounded-2xl p-5 mb-5 overflow-hidden relative isolate';
                          case 'plain': return 'py-2 mb-2 relative isolate';
                          default: return 'bg-white border-2 border-transparent rounded-2xl mb-5 transition-all hover:border-gray-100 overflow-hidden relative isolate';
                        }
                      };

                      const result: React.ReactNode[] = [];

                      sections.forEach((section, sIdx) => {
                        const style = section.divider?.settings?.sectionStyle as string | undefined;
                        const label = section.divider?.settings?.sectionLabel as string | undefined;
                        const icon = section.divider?.settings?.sectionIcon as string | undefined;
                        const dIdx = section.dividerIdx ?? -1;

                        // Render divider if it exists
                        if (section.divider) {
                          result.push(
                            <React.Fragment key={`divider-${section.divider._id ?? dIdx}`}>
                              {overIdx === dIdx && <motion.div layoutId="drop-indicator" initial={{ opacity: 0, scaleX: 0.8 }} animate={{ opacity: [0.7, 1, 0.7], scaleX: 1 }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }} className="h-1.5 w-full bg-brand-500 rounded-full my-2 shadow-[0_0_8px_var(--brand-500)]" />}
                              <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ type: "spring", stiffness: 300, damping: 25 }} className="mb-3">
                                <FieldCard
                                  field={section.divider}
                                  index={dIdx}
                                  selected={selectedIdx === dIdx}
                                  onSelect={() => { setSelectedIdx(dIdx); setRightPanel("field"); }}
                                  onChange={f => updateField(dIdx, f)}
                                  onDelete={() => deleteField(dIdx)}
                                  onDuplicate={() => duplicateField(dIdx)}
                                  onMoveUp={() => moveField(dIdx, dIdx - 1)}
                                  onMoveDown={() => moveField(dIdx, dIdx + 1)}
                                  isFirst={dIdx === 0}
                                  isLast={dIdx === fields.length - 1}
                                  dragStart={onDragStart(dIdx)}
                                  dragOver={onDragOver(dIdx)}
                                  drop={onDrop(dIdx)}
                                  dragEnd={onDragEnd}
                                  previewLang={previewLanguage}
                                />
                              </motion.div>
                            </React.Fragment>
                          );
                        }

                        // Skip if no fields in section
                        if (section.sectionFields.length === 0) return;

                        // Build section wrapper with proper spacing
                        const innerNodes: React.ReactNode[] = [];
                        let i = 0;
                        while (i < section.sectionFields.length) {
                          const field = section.sectionFields[i];
                          const idx = fields.indexOf(field);
                          const next = section.sectionFields[i + 1];
                          const nextIdx = next ? fields.indexOf(next) : -1;

                          if (field.width === 'half' && next && next.width === 'half') {
                            innerNodes.push(
                              <React.Fragment key={`pair-${idx}`}>
                                {overIdx === idx && <motion.div layoutId="drop-indicator" initial={{ opacity: 0, scaleX: 0.8 }} animate={{ opacity: [0.7, 1, 0.7], scaleX: 1 }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }} className="h-1.5 w-full bg-brand-500 rounded-full my-2 shadow-[0_0_8px_var(--brand-500)]" />}
                                <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ type: "spring", stiffness: 300, damping: 25 }} className="grid grid-cols-2 gap-3 mb-3">
                                  <FieldCard
                                    field={field}
                                    index={idx}
                                    selected={selectedIdx === idx}
                                    onSelect={() => { setSelectedIdx(idx); setRightPanel("field"); }}
                                    onChange={f => updateField(idx, f)}
                                    onDelete={() => deleteField(idx)}
                                    onDuplicate={() => duplicateField(idx)}
                                    onMoveUp={() => moveField(idx, idx - 1)}
                                    onMoveDown={() => moveField(idx, idx + 1)}
                                    isFirst={idx === 0}
                                    isLast={idx === fields.length - 1}
                                    dragStart={onDragStart(idx)}
                                    dragOver={onDragOver(idx)}
                                    drop={onDrop(idx)}
                                    dragEnd={onDragEnd}
                                    previewLang={previewLanguage}
                                  />
                                  <FieldCard
                                    field={next}
                                    index={nextIdx}
                                    selected={selectedIdx === nextIdx}
                                    onSelect={() => { setSelectedIdx(nextIdx); setRightPanel("field"); }}
                                    onChange={f => updateField(nextIdx, f)}
                                    onDelete={() => deleteField(nextIdx)}
                                    onDuplicate={() => duplicateField(nextIdx)}
                                    onMoveUp={() => moveField(nextIdx, nextIdx - 1)}
                                    onMoveDown={() => moveField(nextIdx, nextIdx + 1)}
                                    isFirst={nextIdx === 0}
                                    isLast={nextIdx === fields.length - 1}
                                    dragStart={onDragStart(nextIdx)}
                                    dragOver={onDragOver(nextIdx)}
                                    drop={onDrop(nextIdx)}
                                    dragEnd={onDragEnd}
                                    previewLang={previewLanguage}
                                  />
                                </motion.div>
                              </React.Fragment>
                            );
                            i += 2;
                            continue;
                          }

                          innerNodes.push(
                            <React.Fragment key={`field-${idx}`}>
                              {overIdx === idx && <motion.div layoutId="drop-indicator" initial={{ opacity: 0, scaleX: 0.8 }} animate={{ opacity: [0.7, 1, 0.7], scaleX: 1 }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }} className="h-1.5 w-full bg-brand-500 rounded-full my-2 shadow-[0_0_8px_var(--brand-500)]" />}
                              <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ type: "spring", stiffness: 300, damping: 25 }} className="mb-3 rounded-2xl overflow-hidden">
                                <FieldCard
                                  field={field}
                                  index={idx}
                                  selected={selectedIdx === idx}
                                  onSelect={() => { setSelectedIdx(idx); setRightPanel("field"); }}
                                  onChange={f => updateField(idx, f)}
                                  onDelete={() => deleteField(idx)}
                                  onDuplicate={() => duplicateField(idx)}
                                  onMoveUp={() => moveField(idx, idx - 1)}
                                  onMoveDown={() => moveField(idx, idx + 1)}
                                  isFirst={idx === 0}
                                  isLast={idx === fields.length - 1}
                                  dragStart={onDragStart(idx)}
                                  dragOver={onDragOver(idx)}
                                  drop={onDrop(idx)}
                                  dragEnd={onDragEnd}
                                  previewLang={previewLanguage}
                                />
                              </motion.div>
                            </React.Fragment>
                          );
                          i++;
                        }

                        // Render the section with its fields
                        if (section.divider) {
                          result.push(
                            <div key={`section-${sIdx}`} className={sectionCls(style)}>
                              {(label || activeForm.is_multi_step) && (
                                <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-gray-200">
                                  {activeForm.is_multi_step && (
                                    <span className="flex-shrink-0 h-7 w-7 rounded-full bg-brand-50 text-brand-600 text-xs font-bold flex items-center justify-center">
                                      {icon || sIdx + 1}
                                    </span>
                                  )}
                                  <p className="text-sm font-bold text-gray-900 font-sora tracking-tight">
                                    {label || `Step ${sIdx + 1}`}
                                  </p>
                                </div>
                              )}
                              <div className="space-y-3">
                                {innerNodes}
                              </div>
                            </div>
                          );
                        } else {
                          // For sections without dividers, just render the fields directly
                          result.push(
                            <div key={`section-${sIdx}`} className="space-y-3">
                              {innerNodes}
                            </div>
                          );
                        }
                      });

                      return (
                        <>
                          <AnimatePresence mode="popLayout">
                            {result}
                          </AnimatePresence>
                          {overIdx === fields.length && (
                            <motion.div layoutId="drop-indicator" initial={{ opacity: 0, scaleX: 0.8 }} animate={{ opacity: [0.7, 1, 0.7], scaleX: 1 }} transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }} className="h-1.5 w-full bg-brand-500 rounded-full mt-2 mb-4 shadow-[0_0_8px_var(--brand-500)]" />
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
                <div className="mt-10 pt-8 border-t-2 border-gray-100 flex justify-end">
                  <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 17 }} className="px-8 py-3.5 rounded-xl text-sm font-bold font-sans text-white shadow-lg transition-all w-full sm:w-auto text-center" style={{ background: activeForm.custom_styles?.btnColor || "var(--brand-600, #4f46e5)", boxShadow: `0 10px 20px -10px ${activeForm.custom_styles?.btnColor || 'rgba(79,70,229,0.5)'}` }}>
                    {mlGet(activeForm.submit_btn_text as MultiLangValue, previewLanguage) || "Submit"}
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
          <div className="h-12 w-full" /> {/* Spacer */}
        </div>

        {/* Right Panel Column */}
        <div
          ref={splitRightRef}
          style={{ '--panel-width': `${splitRightWidth}px` } as React.CSSProperties}
          className={`fixed inset-y-0 right-0 z-50 w-[85vw] max-w-[360px] bg-white border-l border-gray-200 flex flex-col overflow-hidden shadow-2xl transition-transform duration-300 ${rightDrawerOpen ? 'translate-x-0' : 'translate-x-full'} xl:relative xl:translate-x-0 xl:w-[var(--panel-width)] xl:max-w-none xl:shadow-[-4px_0_24px_rgba(0,0,0,0.02)] xl:z-10`}
        >
          {/* Drag Handle (Desktop Only) */}
          <div
            className={`hidden xl:flex absolute left-0 top-0 bottom-0 w-3 -translate-x-1.5 cursor-col-resize items-center justify-center transition-opacity z-50 group`}
            onPointerDown={(e) => splitOnPointerDown(e, 'right')}
            onDoubleClick={() => splitReset('right')}
            title="Drag to resize"
          >
            <div className={`flex flex-col gap-1 items-center justify-center w-1.5 h-12 rounded-full transition-all ${splitDragging === 'right' ? 'bg-brand-500' : showPulse ? 'animate-pulse bg-brand-400' : 'bg-gray-300/60 group-hover:bg-brand-400'}`}>
              <div className={`w-0.5 h-0.5 rounded-full ${splitDragging === 'right' ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
              <div className={`w-0.5 h-0.5 rounded-full ${splitDragging === 'right' ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
              <div className={`w-0.5 h-0.5 rounded-full ${splitDragging === 'right' ? 'bg-white' : 'bg-gray-400 group-hover:bg-white'}`}></div>
            </div>
          </div>
          <div className="flex bg-gray-50/80 p-2 gap-2 border-b border-gray-200 flex-shrink-0 relative">
            <button className="xl:hidden absolute top-2 right-2 p-2 text-gray-500 hover:bg-gray-200 rounded-lg z-10 bg-white shadow-sm border border-gray-200" onClick={() => setRightDrawerOpen(false)}><XCircle className="w-4 h-4" /></button>
            {(["field", "preview"] as const).map(p => (
              <button key={p} onClick={() => setRightPanel(p)} className={`flex-1 py-2.5 px-4 text-xs font-bold uppercase tracking-wider rounded-lg transition-all ${rightPanel === p ? "bg-white text-brand-700 shadow-sm border border-gray-200/60" : "text-gray-500 hover:bg-gray-100 hover:text-gray-800"}`}>
                {p === "field" ? "Settings" : "Preview"}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar">
            {rightPanel === "field"
              ? <SelectedFieldSettings fields={activeForm.fields} selectedIdx={selectedIdx} updateField={updateField} deleteField={deleteField} languages={languages} activeLang={previewLanguage} isMultiStep={activeForm.is_multi_step} />
              : <div className="p-6 bg-gray-50 min-h-full"><LivePreview form={activeForm} lang={previewLanguage} /></div>
            }
          </div>
        </div>
      </div>
    </FullWrap>
  );

  // ── VIEW: SETTINGS ────────────────────────────────────────────────────────────
  if (view === "settings" && activeForm) content = (
    <FullWrap id="settings">
      <WizardBar />
      <div className="flex flex-col lg:flex-row flex-1 overflow-hidden min-h-0 bg-gray-50 relative">
        {/* Mobile Settings Top Bar */}
        <div className="lg:hidden p-4 bg-white border-b border-gray-200 flex justify-between items-center z-10 shadow-sm shrink-0">
          <button onClick={() => setLeftDrawerOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl shadow-sm border border-gray-200 text-gray-700 text-sm font-bold font-sans hover:bg-gray-50">
            <Settings className="h-4 w-4" /> Menu
          </button>
          <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} transition={{ type: "spring", stiffness: 400, damping: 17 }} onClick={() => handleSave("done")} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl shadow-sm text-sm font-bold font-sans hover:bg-brand-700 disabled:opacity-60">
            {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />} Save
          </motion.button>
        </div>

        {leftDrawerOpen && <div className="fixed inset-0 bg-ink-900/20 z-40 lg:hidden backdrop-blur-sm transition-opacity" onClick={() => setLeftDrawerOpen(false)} />}

        <div className={`fixed inset-y-0 left-0 z-50 w-[85vw] max-w-[288px] flex-shrink-0 bg-white border-r border-gray-200 h-full shadow-2xl transition-transform duration-300 ${leftDrawerOpen ? 'translate-x-0' : '-translate-x-full'} lg:relative lg:translate-x-0 lg:w-72 lg:shadow-[4px_0_24px_rgba(0,0,0,0.02)] lg:z-10 flex flex-col`}>
          <button className="lg:hidden absolute top-4 right-4 p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors z-10" onClick={() => setLeftDrawerOpen(false)}><XCircle className="w-5 h-5" /></button>

          <div className="flex-1 min-h-0 overflow-y-auto p-5">
            <div className="mb-4 px-3 pr-8">
              <p className="text-sm font-bold text-gray-900 font-sora tracking-tight truncate mb-1">{mlGet(activeForm.name as any, previewLanguage)}</p>
              <p className="text-[11px] font-semibold text-gray-500 font-sans uppercase tracking-widest">Configuration</p>
            </div>
            <div className="space-y-0.5">
              {visibleSNav.map(n => (
                <button key={n.id} onClick={() => setSettingsNav(n.id)} className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-[13px] font-semibold font-sans transition-all ${settingsNav === n.id ? "bg-brand-50 text-brand-700 shadow-sm border border-brand-100/50" : "text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-transparent"}`}>
                  <n.icon className={`h-3.5 w-3.5 flex-shrink-0 ${settingsNav === n.id ? "text-brand-600" : "text-gray-400"}`} />{n.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sticky bottom-0 z-20 flex-shrink-0 border-t border-gray-100 bg-white/90 backdrop-blur-sm px-4 py-3.5 shadow-[0_-4px_16px_rgba(0,0,0,0.05)]">
            <AnimatePresence>
              {(ok || err) && (
                <motion.div
                  initial={{ opacity: 0, y: 4, height: 0, marginBottom: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto", marginBottom: 10 }}
                  exit={{ opacity: 0, y: 4, height: 0, marginBottom: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold font-sans ${ok ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                    {ok ? <CheckCircle className="h-3 w-3 flex-shrink-0" /> : <XCircle className="h-3 w-3 flex-shrink-0" />}
                    <span className="truncate">{ok || err}</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center gap-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.94 }}
                onClick={() => { setWizardStep("build"); setView("builder"); }}
                title="Back to Builder"
                className="flex items-center justify-center h-11 w-11 flex-shrink-0 rounded-xl border border-gray-200 bg-white text-gray-500 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50 shadow-sm transition-all"
              >
                <ArrowLeft className="h-4 w-4" />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                onClick={() => handleSave("done")}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-bold font-sans text-white shadow-md hover:shadow-lg transition-all disabled:opacity-60 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-700 hover:to-brand-600"
              >
                {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                {saving ? "Saving…" : "Save & Publish"}
              </motion.button>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-[20px] min-w-0 min-h-0 bg-gray-50" onClick={() => setLeftDrawerOpen(false)}>
          <div className="max-w-4xl mx-auto bg-white rounded-3xl border border-gray-200 shadow-lg shadow-gray-200/40 p-6 md:p-8 min-h-[600px]">
            <div className="mb-8 pb-6 border-b border-gray-100 flex items-center gap-4">
              <div className="h-12 w-12 bg-brand-50 rounded-2xl flex items-center justify-center border border-brand-100 shadow-sm">
                {React.createElement(visibleSNav.find(s => s.id === settingsNav)?.icon || Settings, { className: "h-6 w-6 text-brand-600" })}
              </div>
              <div>
                <h2 className="text-2xl font-bold font-sora text-gray-900">{visibleSNav.find(s => s.id === settingsNav)?.label || "Settings"}</h2>
                <p className="text-sm text-gray-500 font-sans mt-1">Configure {visibleSNav.find(s => s.id === settingsNav)?.label?.toLowerCase() || "general"} settings for your form.</p>
              </div>
            </div>

            <div className="settings-content-wrapper">
              {settingsNav === "general" && <GeneralTab form={activeForm} onChange={setActiveForm as any} languages={languages} activeLang={previewLanguage} />}
              {settingsNav === "style" && <StyleTab form={activeForm} onChange={setActiveForm as any} />}
              {settingsNav === "security" && <SecurityTab form={activeForm} onChange={setActiveForm as any} />}
              {settingsNav === "notifications" && <NotificationsTab form={activeForm} onChange={setActiveForm as any} fields={activeForm.fields} />}
              {settingsNav === "embed" && <EmbedTab form={activeForm} />}
              {settingsNav === "integrations" && <IntegrationsTab form={activeForm} onChange={setActiveForm as any} />}
              {settingsNav === "gdpr" && <GdprTab form={activeForm} onChange={setActiveForm as any} />}
              {settingsNav === "approval" && <ApprovalTab form={activeForm} onChange={setActiveForm as any} languages={languages} activeLang={previewLanguage} />}
              {settingsNav === "registration" && <RegistrationTab form={activeForm} onChange={setActiveForm as any} />}
            </div>
          </div>
          <div className="h-12 w-full" />
        </div>
      </div>
    </FullWrap>
  );

  // ── VIEW: SUBMISSIONS ─────────────────────────────────────────────────────────
  if (view === "submissions" && activeForm) {
    // A registration form's own registration_approval_mode ('manual') needs
    // the same pending/approve-reject flow as the generic approval_enabled
    // toggle, even though that flag is a separate setting and may be off.
    const approvalEnabled = !!activeForm.approval_enabled
      || (!!activeForm.is_registration_form && (activeForm.registration_approval_mode ?? 'manual') !== 'auto');
    const requireNote = !!activeForm.approval_require_note;
    const pendingCount = pendingTotal;

    content = (
      <PageWrap id="submissions">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-4 mt-[15px]">
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={() => setView("list")} className="flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900 font-sans"><ArrowLeft className="h-4 w-4" /></button>
            <h2 className="text-base font-bold font-sora text-ink-900">{mlGet(activeForm.name as any, previewLanguage)} — Submissions</h2>
            {unread > 0 && <span className="bg-red-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold">{unread} new</span>}
            {approvalEnabled && pendingCount > 0 && (
              <span className="bg-amber-500 text-white rounded-full px-2 py-0.5 text-[10px] font-bold flex items-center gap-1">
                <Clock className="h-2.5 w-2.5" /> {pendingCount} pending
              </span>
            )}
          </div>
          <button onClick={() => exportCsv(activeForm.id!).catch((e: any) => { setErr(e.message ?? 'Export failed'); setTimeout(() => setErr(''), 4000); })} className="flex items-center justify-center w-full md:w-auto gap-2 px-3 py-2 border border-ink-200 rounded-lg text-xs font-medium font-sans hover:bg-ink-50 bg-white">
            <Download className="h-3.5 w-3.5" />Export CSV
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-500" />
            <input value={searchSub} onChange={e => setSearchSub(e.target.value)} placeholder="Search…"
              className="w-full pl-8 pr-3 py-2 text-xs border border-ink-200 rounded-lg bg-white font-sans focus:border-brand-500 outline-none" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setSubPage(1); }} className="w-full sm:w-auto px-3 py-2 text-xs border border-ink-200 rounded-lg bg-white font-sans">
            <option value="">All</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            {approvalEnabled && <option value="pending">Pending</option>}
            {approvalEnabled && <option value="approved">Approved</option>}
            {approvalEnabled && <option value="rejected">Rejected</option>}
          </select>
        </div>

        {loadingSubmissions ? (
          <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center justify-between gap-4 px-4 py-4 border-b border-ink-100 last:border-0 animate-pulse">
                <div className="space-y-2 flex-1">
                  <div className="h-3 bg-gray-200 rounded w-24" />
                  <div className="h-3 bg-gray-100 rounded w-40" />
                </div>
                <div className="h-7 bg-gray-100 rounded w-20" />
              </div>
            ))}
          </div>
        ) : submissions.length === 0 ? (
          <div className="bg-white rounded-xl border border-ink-200 p-16 text-center">
            <div className="text-4xl mb-3">📭</div>
            <p className="text-sm font-semibold text-ink-900 font-sans">No submissions yet</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-ink-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-sans border-collapse">
                <thead>
                  <tr className="bg-ink-50/70 border-b border-ink-200 text-left">
                    <th className="w-8 px-3 py-2.5" />
                    <th className="px-3 py-2.5 font-bold text-ink-500 uppercase tracking-wider text-[10px]">Status</th>
                    <th className="px-3 py-2.5 font-bold text-ink-500 uppercase tracking-wider text-[10px]">Contact</th>
                    <th className="px-3 py-2.5 font-bold text-ink-500 uppercase tracking-wider text-[10px] whitespace-nowrap">Submitted</th>
                    <th className="px-3 py-2.5 font-bold text-ink-500 uppercase tracking-wider text-[10px] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions
                    .map(sub => {
                      const isOpen = expandedSubId === sub.id;
                      return (
                        <React.Fragment key={sub.id}>
                          <tr
                            onClick={() => setExpandedSubId(isOpen ? null : sub.id)}
                            className={`border-b border-ink-100 last:border-0 cursor-pointer hover:bg-ink-50/60 transition-colors ${sub.status === "new" ? "bg-brand-50/30" : ""}`}
                          >
                            <td className="px-3 py-3">
                              <ChevronRight className={`h-3.5 w-3.5 text-ink-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <StatusBadge status={sub.status} />
                                {activeForm.is_registration_form && sub.shopify_sync_error && (
                                  <span className="group relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200 cursor-help">
                                    <AlertTriangle className="h-2.5 w-2.5" /> Sync failed
                                    <span
                                      role="tooltip"
                                      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-56 -translate-x-1/2 rounded-lg bg-ink-900 px-2.5 py-2 text-[11px] font-normal normal-case leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
                                    >
                                      {cleanShopifyErrorMessage(sub.shopify_sync_error)}
                                      <span className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-ink-900" />
                                    </span>
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3">
                              <p className="font-semibold text-ink-900">{sub.customer_email || "Anonymous"}</p>
                              {sub.approval_note && (
                                <p className="text-[10px] text-ink-500 italic mt-0.5">"{sub.approval_note}"</p>
                              )}
                            </td>
                            <td className="px-3 py-3 text-ink-500 whitespace-nowrap">{new Date(sub.created_at).toLocaleString()}</td>
                            <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-1.5 flex-wrap">
                                {sub.status === "new" && (
                                  <button title="Mark read" onClick={async () => { await updateSubmissionStatus(sub.id, "read"); setSubmissions(p => p.map(s => s.id === sub.id ? { ...s, status: "read" as const } : s)); setUnread(u => Math.max(0, u - 1)); }}
                                    className="p-1.5 rounded-lg hover:bg-ink-100"><Eye className="h-3.5 w-3.5 text-ink-500" /></button>
                                )}
                                {approvalEnabled && ["new", "read", "pending"].includes(sub.status) && (
                                  <>
                                    <button title="Approve" onClick={() => setApprovalModal({ action: "approve", submission: sub })}
                                      className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100"><ThumbsUp className="h-3.5 w-3.5 text-green-700" /></button>
                                    <button title="Reject" onClick={() => setApprovalModal({ action: "reject", submission: sub })}
                                      className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100"><ThumbsDown className="h-3.5 w-3.5 text-red-600" /></button>
                                  </>
                                )}
                                {approvalEnabled && sub.status === "rejected" && (
                                  <button title="Re-approve" onClick={() => setApprovalModal({ action: "approve", submission: sub })}
                                    className="p-1.5 rounded-lg bg-green-50 hover:bg-green-100"><CheckCircle className="h-3.5 w-3.5 text-green-700" /></button>
                                )}
                                <button title="Delete" onClick={async () => { if (!confirm("Delete?")) return; await deleteSubmission(sub.id); setSubmissions(p => p.filter(s => s.id !== sub.id)); }}
                                  className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
                              </div>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr className="border-b border-ink-100 last:border-0 bg-ink-50/40">
                              <td colSpan={5} className="px-4 py-3">
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {Object.entries(sub.data).map(([k, v]) => (
                                    <div key={k} className="bg-white rounded-lg px-3 py-2 border border-ink-100">
                                      <p className="text-[9px] font-bold text-ink-500 uppercase tracking-wider">{k.replace(/_/g, " ")}</p>
                                      <p className="text-xs text-ink-900">{Array.isArray(v) ? v.join(", ") : (v || "—")}</p>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                </tbody>
              </table>
            </div>
            {subTotalPages > 1 && (
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-ink-100 flex-wrap">
                <p className="text-[11px] text-ink-500">
                  Page {subPage} of {subTotalPages} · {subTotal} submission{subTotal === 1 ? "" : "s"}
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    disabled={subPage <= 1}
                    onClick={() => setSubPage(p => Math.max(1, p - 1))}
                    className="px-2.5 py-1.5 rounded-lg border border-ink-200 text-xs font-medium font-sans disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ink-50"
                  >
                    Prev
                  </button>
                  <button
                    disabled={subPage >= subTotalPages}
                    onClick={() => setSubPage(p => Math.min(subTotalPages, p + 1))}
                    className="px-2.5 py-1.5 rounded-lg border border-ink-200 text-xs font-medium font-sans disabled:opacity-40 disabled:cursor-not-allowed hover:bg-ink-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {approvalModal && (
          <ApprovalModal
            action={approvalModal.action}
            submission={approvalModal.submission}
            requireNote={requireNote}
            onConfirm={approvalModal.action === "approve" ? handleApprove : handleReject}
            onClose={() => setApprovalModal(null)}
          />
        )}
      </PageWrap>
    );
  }

  // ── VIEW: ANALYTICS ───────────────────────────────────────────────────────────
  if (view === "analytics" && activeForm) content = (
    <PageWrap id="analytics">
      <div className="flex items-center justify-between mb-6 mt-[15px]">
        <div className="flex items-center gap-3">
          <button onClick={() => setView("list")} className="flex items-center gap-1 text-xs text-ink-500 hover:text-ink-900 font-sans"><ArrowLeft className="h-4 w-4" /></button>
          <h2 className="text-base font-bold font-sora text-ink-900">{mlGet(activeForm.name as any, previewLanguage)} — Analytics</h2>
        </div>
      </div>
      {!analytics ? (
        <div className="flex justify-center py-16"><RefreshCw className="h-6 w-6 animate-spin text-brand-600" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            {[
              { label: "Views", value: analytics.summary.views, icon: Eye, color: "bg-blue-50 text-blue-600" },
              { label: "Submissions", value: analytics.summary.submissions, icon: CheckCircle, color: "bg-green-50 text-green-600" },
              { label: "Conversion", value: analytics.summary.conversion_rate.toFixed(1) + "%", icon: BarChart2, color: "bg-brand-100 text-brand-600" },
              { label: "Spam", value: analytics.summary.spam, icon: AlertTriangle, color: "bg-red-50 text-red-500" },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-ink-200 p-4">
                <div className={`inline-flex h-8 w-8 items-center justify-center rounded-lg mb-3 ${s.color}`}><s.icon className="h-4 w-4" /></div>
                <p className="text-xl font-bold text-ink-900 font-sans">{s.value}</p>
                <p className="text-xs text-ink-500 font-sans">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="bg-white rounded-xl border border-ink-200 p-5">
            <p className="text-sm font-semibold text-ink-900 font-sans mb-4">Daily submissions</p>
            <div className="flex items-end gap-1" style={{ height: 80 }}>
              {analytics.daily.map((d, i) => {
                const max = Math.max(...analytics.daily.map(x => x.submissions), 1);
                return <div key={i} className="flex-1 rounded-sm bg-brand-500/80 hover:bg-brand-500" style={{ height: Math.max(Math.round((d.submissions / max) * 74), d.submissions > 0 ? 2 : 0) }} title={d.date + ": " + d.submissions} />;
              })}
            </div>
          </div>
        </>
      )}
    </PageWrap>
  );

  return (
    <>
      <AnimatePresence mode="wait">{content}</AnimatePresence>
      <ShortcodeGuideButton />
    </>
  );
}